/**
 * Rax Agent Chat View
 *
 * Chat interface for talking to an agent.
 * Wraps gateway chat streaming with Rax styling and rendering behavior.
 */

import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import type { SkillSuggestion } from "../types/chat-types.js";
import {
  raxApi,
  type AgentShare,
  type AgentShareRole,
  type AutomationRule,
  type AutomationRuleDraft,
  type AutomationRuleStatus,
} from "../../api/rax-api.js";
import {
  extractSkillSuggestionsFromText,
  skillSuggestionStyles,
  type SkillSuggestionConnectHandler,
} from "../chat/skill-suggestion.js";
import { toSanitizedMarkdownHtml } from "../markdown.js";
import "../components/integration-setup-popup.js";
import { raxTheme } from "../styles/rax-theme.js";
import {
  normalizeAssistantContent,
  renderToolEventTranscriptLine,
  type StreamToolEvent,
} from "./assistant-content.js";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: Date;
  cost?: number;
}

interface StoredChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: string;
  cost?: number;
}

interface ChatSessionSummary {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  preview?: string;
}

interface ChatImageAttachment {
  id: string;
  dataUrl: string;
  mimeType: string;
  name: string;
  sizeBytes: number;
}

interface ChatDocumentAttachment {
  id: string;
  dataUrl: string;
  mimeType: string;
  name: string;
  sizeBytes: number;
}

// Document MIME types that Claude can process
const DOCUMENT_MIME_TYPES: Record<string, string> = {
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.xls': 'application/vnd.ms-excel',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.doc': 'application/msword',
  '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  '.ppt': 'application/vnd.ms-powerpoint',
  '.pdf': 'application/pdf',
  '.csv': 'text/csv',
};

interface ChatHistoryPayloadMessage {
  role: "user" | "assistant";
  content: string;
}

const SHARE_ROLE_OPTIONS: Array<{ value: AgentShareRole; label: string }> = [
  { value: "viewer", label: "Viewer (read only)" },
  { value: "member", label: "Member (edit)" },
  { value: "collaborator", label: "Collaborator (edit + execute)" },
  { value: "owner", label: "Owner (full control)" },
];

@customElement("rax-agent-chat")
export class RaxAgentChat extends LitElement {
  static override styles = [
    raxTheme,
    // Skill suggestion styles for conversational onboarding
    css([skillSuggestionStyles] as unknown as TemplateStringsArray),
    css`
      :host {
        display: flex;
        flex-direction: column;
        position: relative;
        flex: 1 1 auto;
        height: auto;
        max-height: none;
        min-height: 0;
        background: var(--bg-primary);
        overflow: hidden; /* Ensure no overflow from this component */
      }
      
      /* Header - compact */
      .chat-header {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-3) var(--space-4);
        background: var(--bg-secondary);
        border-bottom: 1px solid var(--border-color);
        position: sticky;
        top: 0;
        z-index: 10;
        flex: 0 0 auto;
      }
      
      .back-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        color: var(--text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .back-btn:hover {
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
      }
      
      .new-session-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: auto;
        min-width: 96px;
        height: 36px;
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        color: var(--text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
        font-size: var(--font-sm);
        font-weight: 600;
        padding: 0 var(--space-3);
        margin-left: auto;
      }
      
      .new-session-btn:hover {
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
        background: rgba(255, 208, 0, 0.1);
      }
      
      .rules-btn,
      .share-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: auto;
        min-width: 84px;
        height: 36px;
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        color: var(--text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
        font-size: var(--font-sm);
        font-weight: 600;
        padding: 0 var(--space-3);
      }
      
      .rules-btn:hover,
      .share-btn:hover {
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
        background: rgba(255, 208, 0, 0.1);
      }
      
      .session-controls {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        margin-left: auto;
      }
      
      .session-label {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        text-transform: uppercase;
        letter-spacing: 0.4px;
      }
      
      .session-select {
        min-width: 180px;
        max-width: 260px;
        padding: var(--space-2) var(--space-3);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        color: var(--text-primary);
        font-size: var(--font-sm);
      }
      
      .session-select:focus {
        outline: none;
        border-color: var(--rax-yellow);
      }
      
      .agent-switcher {
        position: relative;
      }
      
      .agent-switcher-select {
        min-width: 140px;
        max-width: 200px;
        padding: var(--space-2) var(--space-3);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        color: var(--text-primary);
        font-size: var(--font-sm);
        font-weight: 600;
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .agent-switcher-select:hover {
        border-color: var(--rax-yellow);
      }
      
      .agent-switcher-select:focus {
        outline: none;
        border-color: var(--rax-yellow);
        box-shadow: 0 0 0 2px rgba(255, 208, 0, 0.2);
      }
      
      .agent-info {
        flex: 1;
      }
      
      .agent-name {
        font-size: var(--font-lg);
        font-weight: 600;
        color: var(--text-primary);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      
      .agent-icon {
        font-size: 24px;
      }
      
      .agent-status {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        margin-top: 2px;
      }
      
      .agent-status .dot {
        display: inline-block;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        margin-right: var(--space-1);
      }
      
      .agent-status .dot.active {
        background: var(--success);
        box-shadow: 0 0 8px var(--success);
      }
      
      /* Messages Area - compact like ChatGPT */
      .messages {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        overflow-x: hidden;
        padding: var(--space-3) var(--space-4);
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
        scroll-behavior: smooth;
        max-width: 768px;
        margin: 0 auto;
        width: 100%;
      }
      
      /* Ensure scroll is contained within messages area only */
      .messages::-webkit-scrollbar {
        width: 8px;
      }
      
      .messages::-webkit-scrollbar-track {
        background: transparent;
      }
      
      .messages::-webkit-scrollbar-thumb {
        background: var(--border-color);
        border-radius: 4px;
      }
      
      .messages::-webkit-scrollbar-thumb:hover {
        background: var(--rax-yellow);
      }
      
      .message {
        max-width: 100%;
        padding: var(--space-1) 0;
        line-height: 1.5;
        overflow-wrap: anywhere;
        word-wrap: break-word;
        position: relative;
        animation: messageSlideIn 0.15s ease-out;
      }
      
      @keyframes messageSlideIn {
        from {
          opacity: 0;
          transform: translateY(4px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      /* User messages: subtle right-aligned styling - more compact */
      .message.user {
        align-self: flex-end;
        max-width: 85%;
        padding: 6px 10px;
        background: rgba(255, 208, 0, 0.1);
        border-radius: var(--radius-md);
        color: var(--text-primary);
        font-size: var(--font-sm);
      }
      
      /* Assistant messages: clean, no bubble - like ChatGPT/Claude */
      .message.assistant {
        align-self: flex-start;
        background: transparent;
        border: none;
        box-shadow: none;
        color: var(--text-primary);
      }
      
      .message-meta {
        font-size: 10px;
        color: var(--text-tertiary);
        margin-top: var(--space-1);
        display: flex;
        justify-content: flex-start;
        align-items: center;
        gap: var(--space-2);
        font-family: ui-monospace, monospace;
        opacity: 0.6;
      }
      
      .message.user .message-meta {
        justify-content: flex-end;
      }
      
      .message-timestamp {
        display: flex;
        align-items: center;
        gap: var(--space-1);
      }
      
      .message-content {
        white-space: pre-wrap;
        font-size: var(--font-sm);
      }
      
      .message.assistant .message-content {
        white-space: normal;
        font-size: var(--font-sm);
        line-height: 1.6;
        color: var(--text-primary);
      }
      
      .message.assistant .message-content > :first-child {
        margin-top: 0;
      }
      
      .message.assistant .message-content > :last-child {
        margin-bottom: 0;
      }
      
      /* Code Blocks - clean and compact */
      .message.assistant .message-content pre {
        margin: var(--space-3) 0;
        padding: var(--space-3);
        border-radius: var(--radius-sm);
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        overflow-x: auto;
        font-size: 13px;
      }
      
      .message.assistant .message-content code {
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        font-size: 0.9em;
      }
      
      .message.assistant .message-content :not(pre) > code {
        padding: 0.15rem 0.35rem;
        border-radius: 3px;
        background: rgba(255, 208, 0, 0.1);
        color: var(--rax-yellow);
        font-size: 0.9em;
      }
      
      /* Lists */
      .message.assistant .message-content ul,
      .message.assistant .message-content ol {
        margin: var(--space-2) 0;
        padding-left: var(--space-5);
      }
      
      .message.assistant .message-content li {
        margin: var(--space-1) 0;
        line-height: 1.5;
      }
      
      .message.assistant .message-content li::marker {
        color: var(--text-tertiary);
      }
      
      /* Paragraphs */
      .message.assistant .message-content p {
        margin: var(--space-2) 0;
        line-height: 1.6;
      }
      
      /* Links */
      .message.assistant .message-content a {
        color: var(--rax-yellow);
        text-decoration: none;
        border-bottom: 1px dotted var(--rax-yellow);
        transition: all var(--transition-fast);
      }
      
      .message.assistant .message-content a:hover {
        color: var(--rax-gold);
        border-bottom-color: var(--rax-gold);
        text-shadow: 0 0 8px var(--accent-glow);
      }
      
      /* Tables */
      .message.assistant .message-content table {
        width: 100%;
        margin: var(--space-4) calc(-1 * var(--space-2));
        border-collapse: collapse;
        border-radius: var(--radius-md);
        overflow: hidden;
        border: 1px solid var(--border-color);
        box-shadow: var(--shadow-sm);
        background: var(--bg-elevated);
      }
      
      .message.assistant .message-content th,
      .message.assistant .message-content td {
        padding: var(--space-3) var(--space-4);
        text-align: left;
        border-bottom: 1px solid var(--border-color);
        font-size: var(--font-sm);
      }
      
      .message.assistant .message-content th {
        background: linear-gradient(135deg, var(--rax-yellow) 0%, var(--rax-gold) 100%);
        color: var(--rax-black);
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        font-size: var(--font-xs);
      }
      
      .message.assistant .message-content tbody tr:hover {
        background: rgba(255, 208, 0, 0.05);
      }
      
      .message.assistant .message-content tbody tr:last-child td {
        border-bottom: none;
      }
      
      /* Blockquotes */
      .message.assistant .message-content blockquote {
        margin: var(--space-4) 0;
        padding: var(--space-4);
        border-left: 4px solid var(--rax-yellow);
        background: rgba(255, 208, 0, 0.08);
        border-radius: 0 var(--radius-md) var(--radius-md) 0;
        font-style: italic;
        color: var(--text-secondary);
      }
      
      .message.assistant .message-content blockquote::before {
        content: "💡";
        float: left;
        margin: 0 var(--space-2) 0 0;
        font-size: 1.2em;
      }
      
      /* Headers */
      .message.assistant .message-content h1,
      .message.assistant .message-content h2,
      .message.assistant .message-content h3,
      .message.assistant .message-content h4,
      .message.assistant .message-content h5,
      .message.assistant .message-content h6 {
        color: var(--rax-yellow);
        margin: var(--space-4) 0 var(--space-3) 0;
        font-weight: 600;
      }
      
      .message.assistant .message-content h1 {
        font-size: 1.3em;
      }
      .message.assistant .message-content h2 {
        font-size: 1.2em;
      }
      .message.assistant .message-content h3 {
        font-size: 1.1em;
      }
      
      /* Horizontal Rules */
      .message.assistant .message-content hr {
        margin: var(--space-5) 0;
        border: none;
        height: 1px;
        background: linear-gradient(90deg, transparent, var(--rax-yellow), transparent);
      }
      
      /* Thinking Steps - inline tool event display (compact, ChatGPT-style) */
      .thinking-steps-inline {
        display: flex;
        flex-wrap: wrap;
        gap: 4px 8px;
        padding: 4px 0 8px 0;
        margin-bottom: 4px;
        border-bottom: 1px dashed var(--border-color);
      }

      .message.assistant .message-content .thinking-step {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 2px 6px;
        font-size: 11px;
        color: var(--text-tertiary);
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        background: var(--bg-secondary);
        border-radius: var(--radius-sm);
        white-space: nowrap;
      }

      .message.assistant .message-content .thinking-step .step-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 14px;
        height: 14px;
        font-size: 10px;
        flex-shrink: 0;
      }

      .message.assistant .message-content .thinking-step.completed {
        color: var(--text-tertiary);
        opacity: 0.7;
      }

      .message.assistant .message-content .thinking-step.completed .step-icon {
        color: #10b981;
      }

      .message.assistant .message-content .thinking-step.in-progress {
        color: var(--text-secondary);
        background: rgba(255, 208, 0, 0.1);
        border: 1px solid rgba(255, 208, 0, 0.2);
      }

      .message.assistant .message-content .thinking-step.in-progress .step-icon {
        color: var(--rax-yellow);
        animation: pulse-step 1s ease-in-out infinite;
      }

      .message.assistant .message-content .thinking-step.error {
        color: var(--error);
        background: rgba(239, 68, 68, 0.1);
      }

      .message.assistant .message-content .thinking-step.error .step-icon {
        color: var(--error);
      }

      @keyframes pulse-step {
        0%, 100% { opacity: 0.6; }
        50% { opacity: 1; }
      }

      /* Persona Spawn - special styling for sub-agent delegation */
      .message.assistant .message-content .thinking-step.persona-spawn {
        background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(59, 130, 246, 0.1));
        border: 1px solid rgba(139, 92, 246, 0.3);
        color: var(--text-secondary);
        font-weight: 500;
      }

      .message.assistant .message-content .thinking-step.persona-spawn.in-progress {
        background: linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(59, 130, 246, 0.15));
        border-color: rgba(139, 92, 246, 0.4);
      }

      .message.assistant .message-content .thinking-step.persona-spawn .step-icon {
        font-size: 12px;
      }

      /* Thinking/Proposal Phase - special styling for Ajax thinking */
      .message.assistant .message-content .thinking-step.thinking-phase {
        background: linear-gradient(135deg, rgba(255, 208, 0, 0.1), rgba(255, 165, 0, 0.1));
        border: 1px solid rgba(255, 208, 0, 0.3);
        padding: 6px 12px;
        border-radius: 6px;
      }

      .message.assistant .message-content .thinking-step.thinking-phase.in-progress {
        background: linear-gradient(135deg, rgba(255, 208, 0, 0.15), rgba(255, 165, 0, 0.15));
        border-color: rgba(255, 208, 0, 0.5);
      }

      .message.assistant .message-content .thinking-step.thinking-phase .step-icon {
        font-size: 14px;
      }

      /* Proposal content - displayed inline */
      .message.assistant .message-content .proposal-content {
        margin: 8px 0 12px 0;
        padding: 12px 16px;
        background: rgba(255, 208, 0, 0.05);
        border-left: 3px solid var(--rax-yellow);
        border-radius: 0 6px 6px 0;
        font-size: 14px;
        line-height: 1.6;
        color: var(--text-secondary);
      }

      .message.assistant .message-content .proposal-content p {
        margin: 0 0 8px 0;
      }

      .message.assistant .message-content .proposal-content ul,
      .message.assistant .message-content .proposal-content ol {
        margin: 8px 0;
        padding-left: 24px;
      }

      .message.assistant .message-content .proposal-content li {
        margin: 4px 0;
      }

      /* Generated file download button */
      .message.assistant .message-content .generated-file-download {
        display: inline-flex;
        align-items: center;
        gap: 10px;
        margin: 12px 0;
        padding: 14px 18px;
        background: linear-gradient(135deg, rgba(0, 200, 150, 0.12), rgba(0, 180, 130, 0.08));
        border: 1px solid rgba(0, 200, 150, 0.4);
        border-radius: 10px;
        transition: all 0.2s ease;
      }
      .message.assistant .message-content .generated-file-download:hover {
        background: linear-gradient(135deg, rgba(0, 200, 150, 0.2), rgba(0, 180, 130, 0.15));
        border-color: rgba(0, 200, 150, 0.6);
        transform: translateY(-1px);
      }
      .message.assistant .message-content .generated-file-download .file-icon {
        font-size: 24px;
        line-height: 1;
      }
      .message.assistant .message-content .generated-file-download .file-download-link {
        color: var(--rax-green, #00c896);
        text-decoration: none;
        font-weight: 500;
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      .message.assistant .message-content .generated-file-download .file-download-link:hover {
        text-decoration: underline;
      }
      .message.assistant .message-content .generated-file-download .file-download-link strong {
        font-size: 14px;
        color: var(--text-primary);
      }
      .message.assistant .message-content .generated-file-download .download-hint {
        font-size: 11px;
        color: var(--text-tertiary, #888);
        margin-left: auto;
        padding-left: 12px;
      }

      /* Execution Plan - multi-step planning display */
      .message.assistant .message-content .execution-plan {
        margin: 12px 0;
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: 8px;
        overflow: hidden;
      }

      .message.assistant .message-content .execution-plan .plan-header {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 14px;
        background: linear-gradient(135deg, rgba(255, 208, 0, 0.1), rgba(255, 161, 0, 0.05));
        border-bottom: 1px solid var(--border-color);
        font-weight: 600;
        color: var(--text-primary);
        font-size: 13px;
      }

      .message.assistant .message-content .execution-plan .plan-icon {
        font-size: 14px;
      }

      .message.assistant .message-content .execution-plan .plan-steps {
        padding: 8px 0;
      }

      .message.assistant .message-content .execution-plan .plan-step {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 8px 14px;
        border-bottom: 1px solid var(--border-subtle);
        transition: background 0.15s ease;
      }

      .message.assistant .message-content .execution-plan .plan-step:last-child {
        border-bottom: none;
      }

      .message.assistant .message-content .execution-plan .plan-step.in_progress {
        background: rgba(59, 130, 246, 0.05);
      }

      .message.assistant .message-content .execution-plan .plan-step.completed {
        opacity: 0.7;
      }

      .message.assistant .message-content .execution-plan .plan-step.error {
        background: rgba(239, 68, 68, 0.05);
      }

      .message.assistant .message-content .execution-plan .step-number {
        flex: 0 0 22px;
        height: 22px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        font-size: 11px;
        font-weight: 600;
        background: var(--bg-elevated);
        color: var(--text-tertiary);
        border: 2px solid var(--border-color);
      }

      .message.assistant .message-content .execution-plan .step-number.in_progress {
        background: #3b82f6;
        color: white;
        border-color: #3b82f6;
        animation: pulse-step 1.5s infinite;
      }

      .message.assistant .message-content .execution-plan .step-number.completed {
        background: #22c55e;
        color: white;
        border-color: #22c55e;
      }

      .message.assistant .message-content .execution-plan .step-number.error {
        background: #ef4444;
        color: white;
        border-color: #ef4444;
      }

      .message.assistant .message-content .execution-plan .step-text {
        flex: 1;
        font-size: 13px;
        line-height: 1.4;
        color: var(--text-primary);
      }

      .message.assistant .message-content .execution-plan .step-assignee {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 11px;
        color: var(--text-secondary);
        background: var(--bg-elevated);
        padding: 2px 8px;
        border-radius: 10px;
        white-space: nowrap;
      }

      .message.assistant .message-content .execution-plan .assignee-icon {
        font-size: 10px;
      }

      .message-cost {
        font-family: ui-monospace, monospace;
        font-size: 10px;
        color: var(--text-tertiary);
        background: rgba(255, 208, 0, 0.1);
        padding: 1px 6px;
        border-radius: var(--radius-sm);
        border: 1px solid rgba(255, 208, 0, 0.2);
      }
      
      /* Empty State - compact and subtle */
      .empty-state {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: var(--space-6);
        max-width: 500px;
        margin: 0 auto;
      }
      
      .empty-icon {
        font-size: 48px;
        margin-bottom: var(--space-4);
        opacity: 0.6;
      }
      
      .empty-title {
        font-size: var(--font-lg);
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: var(--space-2);
      }
      
      .empty-desc {
        color: var(--text-tertiary);
        max-width: 320px;
        font-size: var(--font-sm);
        line-height: 1.5;
      }
      
      /* Input Area - centered and compact like ChatGPT */
      .input-area {
        padding: var(--space-2) var(--space-3);
        background: var(--bg-primary);
        border-top: 1px solid var(--border-color);
        position: relative;
        flex: 0 0 auto;
        max-width: 768px;
        margin: 0 auto;
        width: 100%;
      }
      
      .attachment-strip {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-2);
        margin-bottom: var(--space-3);
      }
      
      .attachment-chip {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-1) var(--space-2);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        background: var(--bg-elevated);
        color: var(--text-secondary);
        font-size: var(--font-xs);
      }
      
      .attachment-chip button {
        border: none;
        background: transparent;
        color: var(--text-tertiary);
        cursor: pointer;
        font-size: var(--font-xs);
      }
      
      .attachment-chip button:hover {
        color: var(--error);
      }
      
      .attachment-warning {
        margin-bottom: var(--space-2);
        color: var(--error);
        font-size: var(--font-xs);
      }
      
      .input-wrapper {
        display: flex;
        gap: var(--space-3);
        align-items: flex-end;
        position: relative;
      }

      .upload-btn {
        width: 40px;
        height: 40px;
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        background: var(--bg-elevated);
        color: var(--text-secondary);
        font-size: 16px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all var(--transition-fast);
        flex-shrink: 0;
      }

      .upload-btn:hover {
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
      }

      .upload-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .hidden-file-input {
        display: none;
      }
      
      .input-field {
        flex: 1;
        padding: 8px 12px;
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        color: var(--text-primary);
        font-size: var(--font-sm);
        font-family: inherit;
        resize: none;
        min-height: 36px;
        max-height: 140px;
        transition: all var(--transition-fast);
        line-height: 1.4;
      }
      
      .input-field:focus {
        outline: none;
        border-color: var(--rax-yellow);
        box-shadow: 0 0 0 2px rgba(255, 208, 0, 0.1);
      }
      
      .input-field::placeholder {
        color: var(--text-tertiary);
      }
      
      .send-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        background: var(--rax-yellow);
        color: var(--rax-black);
        border: none;
        border-radius: var(--radius-md);
        font-size: 16px;
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .send-btn.queue-mode {
        background: var(--bg-elevated);
        color: var(--rax-yellow);
        border: 2px solid var(--rax-yellow);
        font-size: 20px;
        font-weight: bold;
      }
      
      .queue-indicator {
        position: absolute;
        top: -24px;
        right: 50px;
        background: var(--rax-yellow);
        color: var(--rax-black);
        padding: 2px 8px;
        border-radius: var(--radius-sm);
        font-size: 11px;
        font-weight: 600;
      }
      
      .send-btn:hover:not(:disabled) {
        background: var(--rax-gold);
      }
      
      .send-btn:active:not(:disabled) {
        transform: scale(0.96);
      }
      
      .send-btn:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
      
      /* Typing Indicator - clean text with subtle animation */
      .typing-indicator {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-2) 0;
        color: var(--text-secondary);
        font-size: var(--font-sm);
        animation: fade-pulse 1.5s ease-in-out infinite;
      }
      
      .typing-indicator .typing-text {
        color: var(--text-tertiary);
      }
      
      .typing-dots {
        display: flex;
        align-items: center;
        gap: 3px;
      }
      
      .typing-dot {
        width: 4px;
        height: 4px;
        background: var(--rax-yellow);
        border-radius: 50%;
        animation: dot-bounce 1.2s ease-in-out infinite;
      }
      
      .typing-dot:nth-child(1) {
        animation-delay: 0s;
      }
      .typing-dot:nth-child(2) {
        animation-delay: 0.15s;
      }
      .typing-dot:nth-child(3) {
        animation-delay: 0.3s;
      }
      
      @keyframes dot-bounce {
        0%,
        60%,
        100% {
          transform: translateY(0);
          opacity: 0.4;
        }
        30% {
          transform: translateY(-4px);
          opacity: 1;
        }
      }
      
      @keyframes fade-pulse {
        0%,
        100% {
          opacity: 0.8;
        }
        50% {
          opacity: 1;
        }
      }
      
      /* Polling countdown indicator */
      .polling-countdown {
        position: absolute;
        bottom: 8px;
        left: 8px;
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 4px 10px;
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        font-size: 11px;
        color: var(--text-tertiary);
        opacity: 0.7;
        transition: opacity 0.2s;
      }
      
      .polling-countdown:hover {
        opacity: 1;
      }
      
      .polling-countdown .countdown-icon {
        color: var(--rax-yellow);
        font-size: 12px;
      }
      
      .polling-countdown .countdown-time {
        font-family: monospace;
        color: var(--text-secondary);
      }
      
      .polling-countdown.due {
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
      }
      
      .automation-overlay {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: flex-end;
        justify-content: center;
        padding: var(--space-6);
        background: rgba(0, 0, 0, 0.55);
        z-index: 30;
      }
      
      .automation-modal {
        width: min(680px, 100%);
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        box-shadow: var(--shadow-lg);
        padding: var(--space-5);
        display: grid;
        gap: var(--space-3);
      }
      
      .automation-title {
        font-size: var(--font-lg);
        font-weight: 700;
        color: var(--rax-yellow);
      }
      
      .automation-subtitle {
        color: var(--text-secondary);
        font-size: var(--font-sm);
      }
      
      .automation-field {
        display: grid;
        gap: var(--space-1);
      }
      
      .automation-field label {
        color: var(--text-tertiary);
        font-size: var(--font-xs);
        text-transform: uppercase;
        letter-spacing: 0.4px;
      }
      
      .automation-field input,
      .automation-field select,
      .automation-field textarea {
        width: 100%;
        padding: var(--space-2) var(--space-3);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        background: var(--bg-elevated);
        color: var(--text-primary);
        font-size: var(--font-sm);
      }
      
      .automation-field textarea {
        min-height: 84px;
        resize: vertical;
      }
      
      .automation-actions {
        display: flex;
        justify-content: flex-end;
        gap: var(--space-2);
        margin-top: var(--space-2);
      }
      
      .automation-btn {
        border: 1px solid var(--border-color);
        background: var(--bg-elevated);
        color: var(--text-secondary);
        border-radius: var(--radius-md);
        padding: var(--space-2) var(--space-4);
        font-weight: 600;
        cursor: pointer;
      }
      
      .automation-btn:hover {
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
      }
      
      .automation-btn.confirm {
        background: var(--rax-yellow);
        color: var(--rax-black);
        border-color: transparent;
      }
      
      .automation-btn.confirm:hover {
        background: var(--rax-gold);
        color: var(--rax-black);
      }
      
      .rules-overlay {
        position: absolute;
        inset: 0;
        display: flex;
        justify-content: flex-end;
        background: rgba(0, 0, 0, 0.45);
        z-index: 28;
      }
      
      .rules-panel {
        width: min(420px, 92vw);
        height: 100%;
        background: var(--bg-secondary);
        border-left: 1px solid var(--border-color);
        padding: var(--space-4);
        display: grid;
        grid-template-rows: auto auto 1fr;
        gap: var(--space-3);
      }
      
      .rules-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-2);
      }
      
      .rules-title {
        font-size: var(--font-md);
        font-weight: 700;
        color: var(--rax-yellow);
      }
      
      .rules-close {
        border: 1px solid var(--border-color);
        background: var(--bg-elevated);
        color: var(--text-secondary);
        border-radius: var(--radius-sm);
        padding: var(--space-1) var(--space-2);
        cursor: pointer;
      }
      
      .rules-list {
        overflow: auto;
        display: grid;
        gap: var(--space-2);
      }
      
      .rule-card {
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        background: var(--bg-elevated);
        padding: var(--space-3);
        display: grid;
        gap: var(--space-2);
      }
      
      .rule-name {
        font-size: var(--font-sm);
        font-weight: 700;
        color: var(--text-primary);
      }
      
      .rule-meta {
        font-size: var(--font-xs);
        color: var(--text-secondary);
      }
      
      .rule-actions {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-2);
      }
      
      .rule-status {
        flex: 1;
        padding: var(--space-1) var(--space-2);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-sm);
        background: var(--bg-primary);
        color: var(--text-primary);
        font-size: var(--font-xs);
      }
      
      .rule-delete {
        border: 1px solid var(--border-color);
        background: var(--bg-primary);
        color: var(--text-secondary);
        border-radius: var(--radius-sm);
        padding: var(--space-1) var(--space-2);
        cursor: pointer;
        font-size: var(--font-xs);
      }
      
      .rule-delete:hover {
        border-color: var(--error);
        color: var(--error);
      }
      
      .share-overlay {
        position: absolute;
        inset: 0;
        display: flex;
        justify-content: flex-end;
        background: rgba(0, 0, 0, 0.45);
        z-index: 29;
      }
      
      .share-panel {
        width: min(460px, 94vw);
        height: 100%;
        background: var(--bg-secondary);
        border-left: 1px solid var(--border-color);
        padding: var(--space-4);
        display: grid;
        grid-template-rows: auto auto minmax(120px, 1fr) auto;
        gap: var(--space-3);
      }
      
      .share-list {
        overflow: auto;
        display: grid;
        gap: var(--space-2);
      }
      
      .share-card {
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        background: var(--bg-elevated);
        padding: var(--space-3);
        display: grid;
        gap: var(--space-2);
      }
      
      .share-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-2);
      }
      
      .share-user {
        color: var(--text-primary);
        font-size: var(--font-sm);
        font-weight: 600;
      }
      
      .share-role {
        color: var(--text-secondary);
        font-size: var(--font-xs);
        text-transform: uppercase;
      }
      
      .share-meta {
        color: var(--text-secondary);
        font-size: var(--font-xs);
      }
      
      .share-remove {
        border: 1px solid var(--border-color);
        background: var(--bg-primary);
        color: var(--text-secondary);
        border-radius: var(--radius-sm);
        padding: var(--space-1) var(--space-2);
        cursor: pointer;
        font-size: var(--font-xs);
      }
      
      .share-remove:hover {
        border-color: var(--error);
        color: var(--error);
      }
      
      .share-form {
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        background: var(--bg-elevated);
        padding: var(--space-3);
        display: grid;
        gap: var(--space-2);
      }
      
      .share-inline {
        display: grid;
        grid-template-columns: 2fr 1fr;
        gap: var(--space-2);
      }
      
      .share-scope {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        font-size: var(--font-xs);
        color: var(--text-secondary);
      }
      
      .share-skills {
        max-height: 140px;
        overflow: auto;
        border: 1px solid var(--border-color);
        border-radius: var(--radius-sm);
        padding: var(--space-2);
        display: grid;
        gap: var(--space-1);
        background: var(--bg-primary);
      }
      
      .share-skill {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        font-size: var(--font-xs);
        color: var(--text-secondary);
      }
      
      .share-actions {
        display: flex;
        justify-content: flex-end;
      }
      
      /* Action Approval Modal */
      .action-approval-overlay {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: var(--space-6);
        background: rgba(0, 0, 0, 0.65);
        z-index: 35;
      }
      
      .action-approval-modal {
        width: min(480px, 100%);
        background: var(--bg-secondary);
        border: 2px solid var(--rax-yellow);
        border-radius: var(--radius-lg);
        box-shadow: 0 0 24px rgba(255, 208, 0, 0.2);
        padding: var(--space-5);
        display: grid;
        gap: var(--space-3);
      }
      
      .action-approval-icon {
        font-size: 32px;
        text-align: center;
      }
      
      .action-approval-title {
        font-size: var(--font-lg);
        font-weight: 700;
        color: var(--rax-yellow);
        text-align: center;
      }
      
      .action-approval-subtitle {
        color: var(--text-secondary);
        font-size: var(--font-sm);
        text-align: center;
        line-height: 1.5;
      }
      
      .action-approval-details {
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        padding: var(--space-3);
        font-size: var(--font-sm);
        color: var(--text-primary);
        font-family: ui-monospace, monospace;
      }
      
      .action-approval-actions {
        display: flex;
        justify-content: center;
        gap: var(--space-3);
        margin-top: var(--space-2);
      }
      
      .action-approval-btn {
        min-width: 100px;
        border: 1px solid var(--border-color);
        background: var(--bg-elevated);
        color: var(--text-secondary);
        border-radius: var(--radius-md);
        padding: var(--space-2) var(--space-4);
        font-weight: 600;
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .action-approval-btn:hover {
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
      }
      
      .action-approval-btn.approve {
        background: var(--rax-yellow);
        color: var(--rax-black);
        border-color: transparent;
      }
      
      .action-approval-btn.approve:hover {
        background: var(--rax-gold);
        color: var(--rax-black);
      }
      
      .action-approval-btn.deny {
        border-color: var(--error);
        color: var(--error);
      }
      
      .action-approval-btn.deny:hover {
        background: var(--error);
        color: var(--rax-black);
      }
      
      @media (max-width: 900px) {
        .chat-header {
          gap: var(--space-2);
          padding: var(--space-3) var(--space-4);
        }
      
        .messages {
          padding: var(--space-4);
        }
      
        .message {
          max-width: 92%;
          padding: var(--space-3) var(--space-4);
        }
      
        .session-select {
          min-width: 140px;
          max-width: 180px;
        }
      
        .input-area {
          padding: var(--space-3) var(--space-4);
        }
      
        .input-wrapper {
          gap: var(--space-2);
        }
      
        .input-field {
          font-size: var(--font-sm);
        }
      
        .send-btn {
          width: 46px;
          height: 46px;
          font-size: 18px;
        }
      }
      
      @media (max-width: 640px) {
        .chat-header {
          display: grid;
          grid-template-columns: auto auto 1fr;
          gap: var(--space-2);
          align-items: center;
        }
      
        .agent-info {
          grid-column: 1 / -1;
          order: 4;
        }
      
        .agent-switcher {
          grid-column: 2;
        }
      
        .agent-switcher-select {
          min-width: 0;
          max-width: 160px;
          padding: var(--space-1) var(--space-2);
          font-size: var(--font-xs);
          height: 34px;
        }
      
        .session-controls {
          margin-left: 0;
          justify-content: flex-end;
        }
      
        .session-label {
          display: none;
        }
      
        .session-select {
          min-width: 0;
          max-width: none;
          width: 100%;
        }
      
        .rules-btn,
        .share-btn,
        .new-session-btn {
          min-width: 0;
          padding: 0 var(--space-2);
          height: 34px;
          font-size: var(--font-xs);
        }
      
        .new-session-btn {
          margin-left: 0;
        }
      
        .messages {
          padding: var(--space-3);
          gap: var(--space-3);
        }
      
        .message {
          max-width: 100%;
        }
      
        .message-meta {
          font-size: 11px;
          padding-top: var(--space-1);
          margin-top: var(--space-2);
        }
      
        .empty-title {
          font-size: var(--font-xl);
        }
      
        .empty-desc {
          font-size: var(--font-sm);
        }
      }
    `,
  ];

  @property({ type: String })
  agentId = "";

  @property({ type: String })
  agentName = "Agent";

  @property({ type: String })
  agentIcon = "⚡";

  /** List of all available agents for the switcher dropdown */
  @property({ type: Array })
  availableAgents: Array<{ id: string; name: string; icon: string }> = [];

  @state()
  private messages: ChatMessage[] = [];

  @state()
  private inputValue = "";

  @state()
  private sending = false;

  @state()
  private typing = false;

  @state()
  private thinkingStatus = "Thinking";

  @state()
  private sessionId = "";

  @state()
  private sessions: ChatSessionSummary[] = [];

  @state()
  private showAutomationConfirm = false;

  @state()
  private automationSaving = false;

  @state()
  private automationName = "";

  @state()
  private automationTriggerType: "schedule" | "event" = "schedule";

  @state()
  private automationSchedule = "";

  @state()
  private automationEventCondition = "";

  @state()
  private automationAction = "";

  @state()
  private automationSourceMessage = "";

  @state()
  private showAutomationRules = false;

  @state()
  private automationRulesLoading = false;

  @state()
  private automationRulesError: string | null = null;

  @state()
  private automationRules: AutomationRule[] = [];

  @state()
  private showSharePanel = false;

  @state()
  private shareLoading = false;

  @state()
  private shareError: string | null = null;

  @state()
  private shares: AgentShare[] = [];

  @state()
  private shareInviteEmail = "";

  @state()
  private shareInviteRole: AgentShareRole = "collaborator";

  @state()
  private shareScopeMode: "all" | "scoped" = "all";

  @state()
  private shareWriteAccess = true;

  @state()
  private availableSkills: string[] = [];

  @state()
  private selectedScopeSkills: string[] = [];

  @state()
  private shareSubmitting = false;

  @state()
  private imageAttachments: ChatImageAttachment[] = [];

  @state()
  private documentAttachments: ChatDocumentAttachment[] = [];

  @state()
  private attachmentWarning: string | null = null;

  @state()
  private showIntegrationSetup = false;

  @state()
  private integrationChannelId: string | null = null;

  @state()
  private pendingActionApproval: {
    toolName: string;
    taskKey: string;
    reason: string;
    actionClass: string;
  } | null = null;

  // Message queue - allows typing next message while agent is working
  @state()
  private messageQueue: Array<{ content: string; images: ChatImageAttachment[] }> = [];

  // Polling status for countdown display
  @state()
  private pollingSecondsUntilNext: number | null = null;

  @state()
  private pollingChannelId: string | null = null;

  private pollingStatusInterval: ReturnType<typeof setInterval> | null = null;

  override connectedCallback() {
    super.connectedCallback();
    this.initializeSessionState();
    this.startPollingStatusRefresh();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.stopPollingStatusRefresh();
  }

  private lastPollingStatusFetch = 0;

  private startPollingStatusRefresh() {
    // Initial fetch
    void this.refreshPollingStatus();
    this.lastPollingStatusFetch = Date.now();
    // Update every second for countdown
    this.pollingStatusInterval = setInterval(() => {
      if (this.pollingSecondsUntilNext !== null && this.pollingSecondsUntilNext > 0) {
        this.pollingSecondsUntilNext--;
      } else if (this.pollingSecondsUntilNext === 0) {
        // Countdown reached 0, refresh from server
        void this.refreshPollingStatus();
        this.lastPollingStatusFetch = Date.now();
      } else {
        // No active polling - only refresh every 30 seconds
        const timeSinceLastFetch = Date.now() - this.lastPollingStatusFetch;
        if (timeSinceLastFetch >= 30000) {
          void this.refreshPollingStatus();
          this.lastPollingStatusFetch = Date.now();
        }
      }
    }, 1000);
  }

  private stopPollingStatusRefresh() {
    if (this.pollingStatusInterval) {
      clearInterval(this.pollingStatusInterval);
      this.pollingStatusInterval = null;
    }
  }

  private async refreshPollingStatus() {
    try {
      const result = await raxApi.getPollingStatus();
      if (result.success && result.data?.nextPoll) {
        this.pollingSecondsUntilNext = result.data.nextPoll.secondsUntilNextPoll;
        this.pollingChannelId = result.data.nextPoll.channelId;
      } else {
        this.pollingSecondsUntilNext = null;
        this.pollingChannelId = null;
      }
    } catch {
      // Silently ignore polling status errors
    }
  }

  override updated(changedProps: Map<string, unknown>) {
    if (changedProps.has("agentId")) {
      this.initializeSessionState();
    }
  }

  override firstUpdated() {
    this.scrollToBottom(true); // Force scroll on initial load
  }

  private getSessionPointerStorageKey(): string {
    return `rax.chat.session.${this.agentId}`;
  }

  private getSessionListStorageKey(): string {
    return `rax.chat.sessions.${this.agentId}`;
  }

  private getSessionMessagesStorageKey(sessionId: string): string {
    return `rax.chat.messages.${this.agentId}.${sessionId}`;
  }

  private createSessionId(): string {
    return `rax-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  private initializeSessionState() {
    if (!this.agentId) {
      this.sessions = [];
      this.sessionId = "";
      this.messages = [];
      this.showSharePanel = false;
      this.shares = [];
      this.availableSkills = [];
      return;
    }

    let sessions = this.loadSessionsFromStorage();
    const pointerKey = this.getSessionPointerStorageKey();
    let activeSessionId = localStorage.getItem(pointerKey) ?? "";

    const hasActiveSession =
      activeSessionId && sessions.some((session) => session.id === activeSessionId);
    if (!hasActiveSession) {
      if (sessions.length === 0) {
        const createdAt = new Date().toISOString();
        const firstSession: ChatSessionSummary = {
          id: this.createSessionId(),
          title: "Session 1",
          createdAt,
          updatedAt: createdAt,
        };
        sessions = [firstSession];
        this.saveSessionsToStorage(sessions);
      }
      activeSessionId = sessions[0].id;
      localStorage.setItem(pointerKey, activeSessionId);
    }

    this.sessions = sessions;
    this.sessionId = activeSessionId;
    this.messages = this.loadMessagesFromStorage(activeSessionId);
    this.imageAttachments = [];
    this.documentAttachments = [];
    this.attachmentWarning = null;
    this.showSharePanel = false;
    this.shareError = null;
    this.shares = [];
    this.availableSkills = [];
    this.selectedScopeSkills = [];
    this.scrollToBottom(true);
  }

  private loadSessionsFromStorage(): ChatSessionSummary[] {
    try {
      const raw = localStorage.getItem(this.getSessionListStorageKey());
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed
        .filter((value): value is ChatSessionSummary => {
          return (
            value &&
            typeof value.id === "string" &&
            typeof value.title === "string" &&
            typeof value.createdAt === "string" &&
            typeof value.updatedAt === "string"
          );
        })
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    } catch {
      return [];
    }
  }

  private saveSessionsToStorage(sessions: ChatSessionSummary[]) {
    localStorage.setItem(this.getSessionListStorageKey(), JSON.stringify(sessions));
  }

  private loadMessagesFromStorage(sessionId: string): ChatMessage[] {
    try {
      const raw = localStorage.getItem(this.getSessionMessagesStorageKey(sessionId));
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        return [];
      }
      return parsed
        .filter((value): value is StoredChatMessage => {
          return (
            value &&
            typeof value.id === "string" &&
            typeof value.role === "string" &&
            typeof value.content === "string" &&
            typeof value.timestamp === "string"
          );
        })
        .map((value) => ({
          ...value,
          timestamp: new Date(value.timestamp),
        }));
    } catch {
      return [];
    }
  }

  private saveMessagesToStorage(sessionId: string, messages: ChatMessage[]) {
    const serializable: StoredChatMessage[] = messages.map((message) => ({
      ...message,
      timestamp: message.timestamp.toISOString(),
    }));
    localStorage.setItem(
      this.getSessionMessagesStorageKey(sessionId),
      JSON.stringify(serializable),
    );
    // NOTE: Server sync disabled - raxclaw agent owns its own history
    // localStorage is the source of truth for the UI
  }

  // ========== Server Sync Methods ==========

  // Track which messages have been synced to avoid duplicate POSTs
  private syncedMessageIds = new Set<string>();
  private syncDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingSyncMessages: ChatMessage[] = [];

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const clerk = (window as any).clerkClient || (window as any).Clerk;
    const token = clerk?.session ? await clerk.session.getToken() : null;
    return {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  private async syncMessagesToServer(sessionId: string, messages: ChatMessage[]) {
    // Filter to only messages that haven't been synced yet
    const unsyncedMessages = messages.filter((msg) => !this.syncedMessageIds.has(msg.id));
    if (unsyncedMessages.length === 0) {
      return;
    }

    // Debounce: accumulate messages and sync after 500ms of inactivity
    this.pendingSyncMessages = unsyncedMessages;
    if (this.syncDebounceTimer) {
      clearTimeout(this.syncDebounceTimer);
    }

    this.syncDebounceTimer = setTimeout(async () => {
      const toSync = this.pendingSyncMessages;
      this.pendingSyncMessages = [];
      this.syncDebounceTimer = null;

      if (toSync.length === 0) return;

      const headers = await this.getAuthHeaders();
      for (const msg of toSync) {
        // Skip if already synced (race condition protection)
        if (this.syncedMessageIds.has(msg.id)) continue;

        try {
          await fetch(`/api/rax/chat/sessions/${sessionId}/messages`, {
            method: "POST",
            headers,
            body: JSON.stringify({
              id: msg.id,
              role: msg.role,
              content: msg.content,
              cost: msg.cost,
            }),
          });
          // Mark as synced only on success
          this.syncedMessageIds.add(msg.id);
        } catch {
          // Will retry on next persist
        }
      }
    }, 500);
  }

  private async syncSessionToServer(session: ChatSessionSummary) {
    try {
      const headers = await this.getAuthHeaders();
      await fetch("/api/rax/chat/sessions", {
        method: "POST",
        headers,
        body: JSON.stringify({
          id: session.id,
          agentId: this.agentId,
          title: session.title,
        }),
      });
    } catch {
      // Ignore - localStorage is fallback
    }
  }

  private async loadSessionsFromServer(): Promise<ChatSessionSummary[] | null> {
    try {
      const headers = await this.getAuthHeaders();
      const res = await fetch(
        `/api/rax/chat/sessions?agentId=${encodeURIComponent(this.agentId || "")}`,
        { headers },
      );
      if (!res.ok) {
        return null;
      }
      const data = await res.json();
      if (!data.success || !Array.isArray(data.sessions)) {
        return null;
      }
      return data.sessions.map(
        (s: { id: string; title?: string; createdAt: string; updatedAt: string; preview?: string }) => ({
          id: s.id,
          title: s.title || "Untitled",
          createdAt: s.createdAt,
          updatedAt: s.updatedAt,
          preview: s.preview,
        }),
      );
    } catch {
      return null;
    }
  }

  private async loadMessagesFromServer(sessionId: string): Promise<ChatMessage[] | null> {
    try {
      const headers = await this.getAuthHeaders();
      const res = await fetch(`/api/rax/chat/sessions/${sessionId}/messages`, { headers });
      if (!res.ok) {
        return null;
      }
      const data = await res.json();
      if (!data.success || !Array.isArray(data.messages)) {
        return null;
      }
      return data.messages.map(
        (m: { id: string; role: string; content: string; cost?: number; timestamp: string }) => ({
          id: m.id,
          role: m.role as "user" | "assistant" | "system",
          content: m.content,
          cost: m.cost,
          timestamp: new Date(m.timestamp),
        }),
      );
    } catch {
      return null;
    }
  }

  private updateSessionMetadata(sessionId: string, messages: ChatMessage[]) {
    const lastContent = [...messages]
      .reverse()
      .find((message) => message.role !== "system" && message.content.trim().length > 0)?.content;
    const firstUserContent = messages.find(
      (message) => message.role === "user" && message.content.trim().length > 0,
    )?.content;

    const nextSessions = this.sessions
      .map((session) => {
        if (session.id !== sessionId) {
          return session;
        }
        const generatedTitle = firstUserContent
          ? firstUserContent.trim().slice(0, 42)
          : session.title;
        return {
          ...session,
          title:
            session.title.toLowerCase().startsWith("session ") && generatedTitle
              ? generatedTitle
              : session.title,
          updatedAt: new Date().toISOString(),
          preview: lastContent ? lastContent.trim().slice(0, 120) : session.preview,
        };
      })
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

    this.sessions = nextSessions;
    this.saveSessionsToStorage(nextSessions);
  }

  private persistCurrentSession() {
    if (!this.sessionId) {
      return;
    }
    this.saveMessagesToStorage(this.sessionId, this.messages);
    this.updateSessionMetadata(this.sessionId, this.messages);
    localStorage.setItem(this.getSessionPointerStorageKey(), this.sessionId);
  }

  private startNewSession() {
    const createdAt = new Date().toISOString();
    const newSession: ChatSessionSummary = {
      id: this.createSessionId(),
      title: `Session ${this.sessions.length + 1}`,
      createdAt,
      updatedAt: createdAt,
    };
    const nextSessions = [newSession, ...this.sessions];
    this.sessions = nextSessions;
    this.sessionId = newSession.id;
    this.messages = [];
    this.imageAttachments = [];
    this.documentAttachments = [];
    this.attachmentWarning = null;
    this.saveSessionsToStorage(nextSessions);
    this.saveMessagesToStorage(newSession.id, []);
    localStorage.setItem(this.getSessionPointerStorageKey(), newSession.id);
  }

  private switchSession(nextSessionId: string) {
    if (!nextSessionId || nextSessionId === this.sessionId) {
      return;
    }
    if (!this.sessions.some((session) => session.id === nextSessionId)) {
      return;
    }
    this.sessionId = nextSessionId;
    this.messages = this.loadMessagesFromStorage(nextSessionId);
    this.imageAttachments = [];
    this.documentAttachments = [];
    this.attachmentWarning = null;
    this.typing = false;
    this.sending = false;
    localStorage.setItem(this.getSessionPointerStorageKey(), nextSessionId);
    this.scrollToBottom(true);
  }

  override render() {
    return html`
      ${this.renderHeader()}
      ${this.messages.length === 0 ? this.renderEmptyState() : this.renderMessages()}
      ${this.renderInput()}
      ${this.showAutomationRules ? this.renderAutomationRulesPanel() : ""}
      ${this.showSharePanel ? this.renderSharePanel() : ""}
      ${this.showAutomationConfirm ? this.renderAutomationConfirmModal() : ""}
      ${
        this.showIntegrationSetup && this.integrationChannelId
          ? html`
        <rax-integration-setup-popup
          .draft=${{ channelId: this.integrationChannelId }}
          @cancel=${this.handleIntegrationCancel}
          @saved=${(e: CustomEvent) => this.handleIntegrationSaved(e.detail)}
        ></rax-integration-setup-popup>
      `
          : ""
      }
      ${this.pendingActionApproval ? this.renderActionApprovalModal() : ""}
    `;
  }

  private renderHeader() {
    const showAgentSwitcher = this.availableAgents.length > 1;

    return html`
      <div class="chat-header">
        <button class="back-btn" @click=${this.handleBack}>
          ←
        </button>
        ${
          showAgentSwitcher
            ? html`
          <div class="agent-switcher">
            <select
              class="agent-switcher-select"
              .value=${this.agentId}
              @change=${this.handleAgentSwitch}
              title="Switch Agent"
            >
              ${this.availableAgents.map(
                (agent) => html`
                <option value=${agent.id}>
                  ${agent.icon} ${agent.name}
                </option>
              `,
              )}
            </select>
          </div>
        `
            : html`
          <div class="agent-info">
            <div class="agent-name">
              <span class="agent-icon">${this.agentIcon}</span>
              ${this.agentName}
            </div>
            <div class="agent-status">
              <span class="dot active"></span>
              Online
              ${
                this.sessionId
                  ? html`<span style="font-family: monospace; margin-left: 8px; opacity: 0.6;">(${this.sessionId.slice(-8)})</span>`
                  : ""
              }
            </div>
          </div>
        `
        }
        <div class="session-controls">
          <label class="session-label" for="session-picker">Session</label>
          <select
            id="session-picker"
            class="session-select"
            .value=${this.sessionId}
            @change=${this.handleSessionChange}
          >
            ${this.sessions.map(
              (session) => html`
              <option value=${session.id}>
                ${session.title}
              </option>
            `,
            )}
          </select>
        </div>
        <button class="rules-btn" @click=${this.toggleAutomationRules} title="Automation Rules">
          Rules
        </button>
        <button class="share-btn" @click=${this.toggleSharePanel} title="Share Agent">
          Share
        </button>
        <button class="new-session-btn" @click=${this.handleNewSession} title="Start New Conversation">
          + New chat
        </button>
      </div>
    `;
  }

  private renderEmptyState() {
    return html`
      <div class="empty-state">
        <div class="empty-icon">${this.agentIcon}</div>
        <h3 class="empty-title">Chat with ${this.agentName}</h3>
        <p class="empty-desc">
          Send a message to start the conversation. Your agent is ready to help.
        </p>
      </div>
    `;
  }

  private renderMessages() {
    return html`
      <div class="messages">
        ${this.messages.map((msg) => this.renderMessage(msg))}
        ${this.typing ? this.renderTypingIndicator() : ""}
      </div>
    `;
  }

  private renderMessage(msg: ChatMessage) {
    return html`
      <div class="message ${msg.role}">
        <div class="message-content">${this.renderMessageContent(msg)}</div>
        <div class="message-meta">
          <span class="message-timestamp">${this.formatTime(msg.timestamp)}</span>
          ${msg.cost ? html`<span class="message-cost">${this.formatCost(msg.cost)}</span>` : ""}
        </div>
      </div>
    `;
  }

  private renderMessageContent(msg: ChatMessage) {
    if (msg.role !== "assistant") {
      return msg.content;
    }

    const content = normalizeAssistantContent(msg.content);

    // Extract skill suggestions from assistant messages
    const skillSuggestions = extractSkillSuggestionsFromText(content);

    return html`
      ${unsafeHTML(toSanitizedMarkdownHtml(content))}
      ${
        skillSuggestions.length > 0
          ? html`
            <div class="skill-suggestions">
              ${skillSuggestions.map(
                (suggestion) => html`
                  <div class="skill-suggestion" data-skill-id="${suggestion.skillId}">
                    <div class="skill-suggestion__icon">
                      ${this.getSkillIcon(suggestion.skillId)}
                    </div>
                    <div class="skill-suggestion__content">
                      <div class="skill-suggestion__header">
                        <span class="skill-suggestion__name">${suggestion.skillName}</span>
                      </div>
                      <div class="skill-suggestion__reason">${suggestion.reason}</div>
                    </div>
                    <button
                      class="skill-suggestion__connect-btn"
                      type="button"
                      @click=${() => this.handleSkillConnect(suggestion)}
                    >
                      Connect ${suggestion.skillName}
                    </button>
                  </div>
                `,
              )}
            </div>
          `
          : ""
      }
    `;
  }

  private getSkillIcon(skillId: string): string {
    const normalized = skillId.toLowerCase();
    if (normalized.includes("gmail") || normalized.includes("email")) {
      return "\u2709\ufe0f"; // envelope
    }
    if (normalized.includes("calendar")) {
      return "\ud83d\udcc5"; // calendar
    }
    if (normalized.includes("slack")) {
      return "\ud83d\udcac"; // speech bubble
    }
    if (normalized.includes("notion")) {
      return "\ud83d\udcdd"; // memo
    }
    if (normalized.includes("github")) {
      return "\ud83d\udc19"; // octopus
    }
    return "\u26a1"; // lightning bolt
  }

  private handleSkillConnect(suggestion: SkillSuggestion) {
    // Map skill suggestion to integration channel IDs
    const integrationMap: Record<string, string> = {
      gmail: "gmail",
      calendar: "google_calendar",
      slack: "slack",
      notion: "notion",
      github: "github",
    };

    const channelId = integrationMap[suggestion.skillId] || suggestion.authProvider;
    if (channelId) {
      // Open the integration setup popup
      this.integrationChannelId = channelId;
      this.showIntegrationSetup = true;
    } else {
      console.warn(`[RaxAgentChat] No integration mapping for skill: ${suggestion.skillId}`);
    }
  }

  private renderTypingIndicator() {
    return html`
      <div class="typing-indicator">
        <span class="typing-text">${this.thinkingStatus || "Thinking"}</span>
        <div class="typing-dots">
          <div class="typing-dot"></div>
          <div class="typing-dot"></div>
          <div class="typing-dot"></div>
        </div>
      </div>
    `;
  }

  private renderInput() {
    return html`
      <div class="input-area">
        ${
          this.imageAttachments.length > 0 || this.documentAttachments.length > 0
            ? html`
              <div class="attachment-strip">
                ${this.imageAttachments.map(
                  (attachment) => html`
                    <div class="attachment-chip">
                      <span>🖼️ ${attachment.name}</span>
                      <button
                        @click=${() => this.removeImageAttachment(attachment.id)}
                        title="Remove image"
                      >
                        ✕
                      </button>
                    </div>
                  `,
                )}
                ${this.documentAttachments.map(
                  (attachment) => html`
                    <div class="attachment-chip">
                      <span>📄 ${attachment.name}</span>
                      <button
                        @click=${() => this.removeDocumentAttachment(attachment.id)}
                        title="Remove document"
                      >
                        ✕
                      </button>
                    </div>
                  `,
                )}
              </div>
            `
            : ""
        }
        ${
          this.attachmentWarning
            ? html`<div class="attachment-warning">${this.attachmentWarning}</div>`
            : ""
        }
        <div class="input-wrapper">
          ${
            this.messageQueue.length > 0
              ? html`<div class="queue-indicator">${this.messageQueue.length} queued</div>`
              : ""
          }
          <input
            type="file"
            class="hidden-file-input"
            accept="image/*,.pdf,.doc,.docx,.txt,.md,.json,.csv,.xlsx,.xls,.pptx,.ppt"
            multiple
            @change=${this.handleFileInputChange}
          />
          <button
            class="upload-btn"
            @click=${this.handleUploadClick}
            ?disabled=${this.sending}
            title="Upload file"
          >📎</button>
          <textarea
            class="input-field"
            placeholder=${
              this.sending
                ? "Type next message... (will be queued)"
                : "Type a message or attach files..."
            }
            .value=${this.inputValue}
            @input=${this.handleInput}
            @keydown=${this.handleKeydown}
            @paste=${this.handleInputPaste}
            rows="1"
          ></textarea>
          <button
            class="send-btn ${this.sending ? "queue-mode" : ""}"
            @click=${this.handleSend}
            ?disabled=${!this.inputValue.trim() && this.imageAttachments.length === 0}
            title=${this.sending ? "Queue message" : "Send message"}
          >
            ${this.sending ? "+" : "⚡"}
          </button>
        </div>
        ${this.renderPollingCountdown()}
      </div>
    `;
  }

  private renderPollingCountdown() {
    if (this.pollingSecondsUntilNext === null || !this.pollingChannelId) {
      return "";
    }

    const isDue = this.pollingSecondsUntilNext <= 0;
    const minutes = Math.floor(this.pollingSecondsUntilNext / 60);
    const seconds = this.pollingSecondsUntilNext % 60;
    const timeDisplay =
      minutes > 0 ? `${minutes}:${seconds.toString().padStart(2, "0")}` : `${seconds}s`;

    const channelLabel =
      this.pollingChannelId === "sendblue-imessage" ? "iMessage" : this.pollingChannelId;

    return html`
      <div class="polling-countdown ${isDue ? "due" : ""}" title="Next ${channelLabel} poll check">
        <span class="countdown-icon">📬</span>
        <span class="countdown-time">${isDue ? "checking..." : timeDisplay}</span>
      </div>
    `;
  }

  private renderAutomationConfirmModal() {
    return html`
      <div class="automation-overlay" @click=${this.handleAutomationOverlayClick}>
        <div class="automation-modal" @click=${(event: Event) => event.stopPropagation()}>
          <div class="automation-title">Confirm automation rule</div>
          <div class="automation-subtitle">
            We can definitely set that up. Confirm the details below before activation.
          </div>

          <div class="automation-field">
            <label>Rule name</label>
            <input
              .value=${this.automationName}
              @input=${(e: Event) => (this.automationName = (e.target as HTMLInputElement).value)}
            />
          </div>

          <div class="automation-field">
            <label>Trigger type</label>
            <select
              .value=${this.automationTriggerType}
              @change=${(e: Event) =>
                (this.automationTriggerType = (e.target as HTMLSelectElement).value as
                  | "schedule"
                  | "event")}
            >
              <option value="schedule">Schedule</option>
              <option value="event">Event</option>
            </select>
          </div>

          ${
            this.automationTriggerType === "schedule"
              ? html`
                <div class="automation-field">
                  <label>Schedule</label>
                  <input
                    .value=${this.automationSchedule}
                    @input=${(e: Event) =>
                      (this.automationSchedule = (e.target as HTMLInputElement).value)}
                  />
                </div>
              `
              : html`
                <div class="automation-field">
                  <label>Event condition</label>
                  <input
                    .value=${this.automationEventCondition}
                    @input=${(e: Event) =>
                      (this.automationEventCondition = (e.target as HTMLInputElement).value)}
                  />
                </div>
              `
          }

          <div class="automation-field">
            <label>Action</label>
            <textarea
              .value=${this.automationAction}
              @input=${(e: Event) => (this.automationAction = (e.target as HTMLTextAreaElement).value)}
            ></textarea>
          </div>

          <div class="automation-actions">
            <button
              class="automation-btn"
              ?disabled=${this.automationSaving}
              @click=${this.cancelAutomationConfirmation}
            >
              Cancel
            </button>
            <button
              class="automation-btn confirm"
              ?disabled=${this.automationSaving}
              @click=${this.confirmAutomationRule}
            >
              ${this.automationSaving ? "Saving..." : "Confirm"}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private handleAutomationOverlayClick = () => {
    if (this.automationSaving) {
      return;
    }
    this.cancelAutomationConfirmation();
  };

  private renderAutomationRulesPanel() {
    return html`
      <div class="rules-overlay" @click=${this.closeAutomationRules}>
        <aside class="rules-panel" @click=${(event: Event) => event.stopPropagation()}>
          <div class="rules-header">
            <div class="rules-title">Automation Rules</div>
            <button class="rules-close" @click=${this.closeAutomationRules}>Close</button>
          </div>
          <div class="automation-subtitle">
            View and manage ongoing trigger rules for this agent.
          </div>
          <div class="rules-list">
            ${
              this.automationRulesLoading
                ? html`
                    <div class="rule-meta">Loading rules...</div>
                  `
                : this.automationRulesError
                  ? html`<div class="rule-meta">⚠️ ${this.automationRulesError}</div>`
                  : this.automationRules.length === 0
                    ? html`
                        <div class="rule-meta">No rules yet.</div>
                      `
                    : this.automationRules.map(
                        (rule) => html`
                      <div class="rule-card">
                        <div class="rule-name">${rule.name}</div>
                        <div class="rule-meta">
                          ${
                            rule.triggerType === "schedule"
                              ? `Schedule: ${String(rule.triggerConfig?.schedule || "not set")}`
                              : `Event: ${String(rule.triggerConfig?.eventCondition || "not set")}`
                          }
                        </div>
                        <div class="rule-meta">Action: ${rule.action}</div>
                        <div class="rule-actions">
                          <select
                            class="rule-status"
                            .value=${rule.status}
                            @change=${(event: Event) =>
                              this.updateAutomationRuleStatus(
                                rule.id,
                                (event.target as HTMLSelectElement).value as AutomationRuleStatus,
                              )}
                          >
                            <option value="active">active</option>
                            <option value="paused">paused</option>
                            <option value="disabled">disabled</option>
                          </select>
                          <button class="rule-delete" @click=${() => this.deleteAutomationRule(rule.id)}>
                            Delete
                          </button>
                        </div>
                      </div>
                    `,
                      )
            }
          </div>
        </aside>
      </div>
    `;
  }

  private renderSharePanel() {
    return html`
      <div class="share-overlay" @click=${this.closeSharePanel}>
        <aside class="share-panel" @click=${(event: Event) => event.stopPropagation()}>
          <div class="rules-header">
            <div class="rules-title">Share Agent</div>
            <button class="rules-close" @click=${this.closeSharePanel}>Close</button>
          </div>

          <div class="automation-subtitle">
            Invite a teammate by email and assign scoped access for this agent.
          </div>

          <div class="share-list">
            ${
              this.shareLoading
                ? html`
                    <div class="rule-meta">Loading shared access...</div>
                  `
                : this.shareError
                  ? html`<div class="rule-meta">⚠️ ${this.shareError}</div>`
                  : this.shares.length === 0
                    ? html`
                        <div class="rule-meta">No shares yet.</div>
                      `
                    : this.shares.map(
                        (share) => html`
                      <div class="share-card">
                        <div class="share-head">
                          <div class="share-user">${share.email || share.name || share.userId}</div>
                          <div class="share-role">${this.formatShareRole(share.role)}</div>
                        </div>
                        <div class="share-meta">${this.describeShareScope(share)}</div>
                        <div class="share-actions">
                          <button
                            class="share-remove"
                            ?disabled=${this.shareSubmitting}
                            @click=${() => this.removeShareAccess(share.userId)}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    `,
                      )
            }
          </div>

          <div class="share-form">
            <div class="automation-field">
              <label>Email</label>
              <input
                type="email"
                placeholder="teammate@company.com"
                .value=${this.shareInviteEmail}
                @input=${(event: Event) =>
                  (this.shareInviteEmail = (event.target as HTMLInputElement).value)}
              />
            </div>

            <div class="share-inline">
              <div class="automation-field">
                <label>Role</label>
                <select
                  .value=${this.shareInviteRole}
                  @change=${(event: Event) =>
                    (this.shareInviteRole = (event.target as HTMLSelectElement)
                      .value as AgentShareRole)}
                >
                  ${SHARE_ROLE_OPTIONS.map(
                    (roleOption) => html`
                    <option value=${roleOption.value}>${roleOption.label}</option>
                  `,
                  )}
                </select>
              </div>

              <div class="automation-field">
                <label>Scope</label>
                <select
                  .value=${this.shareScopeMode}
                  @change=${(event: Event) =>
                    (this.shareScopeMode = (event.target as HTMLSelectElement).value as
                      | "all"
                      | "scoped")}
                >
                  <option value="all">All enabled skills</option>
                  <option value="scoped">Specific skills</option>
                </select>
              </div>
            </div>

            ${
              this.shareScopeMode === "scoped"
                ? html`
                  <label class="share-scope">
                    <input
                      type="checkbox"
                      .checked=${this.shareWriteAccess}
                      @change=${(event: Event) =>
                        (this.shareWriteAccess = (event.target as HTMLInputElement).checked)}
                    />
                    Allow write actions on selected skills
                  </label>
                  <div class="share-skills">
                    ${
                      this.availableSkills.length === 0
                        ? html`
                            <div class="rule-meta">No enabled skills available for scoping.</div>
                          `
                        : this.availableSkills.map(
                            (skillId) => html`
                          <label class="share-skill">
                            <input
                              type="checkbox"
                              .checked=${this.selectedScopeSkills.includes(skillId)}
                              @change=${(event: Event) =>
                                this.toggleScopedSkill(
                                  skillId,
                                  (event.target as HTMLInputElement).checked,
                                )}
                            />
                            ${skillId}
                          </label>
                        `,
                          )
                    }
                  </div>
                `
                : ""
            }

            <div class="share-actions">
              <button
                class="automation-btn confirm"
                ?disabled=${this.shareSubmitting}
                @click=${this.submitShareInvite}
              >
                ${this.shareSubmitting ? "Inviting..." : "Invite"}
              </button>
            </div>
          </div>
        </aside>
      </div>
    `;
  }

  private renderActionApprovalModal() {
    const approval = this.pendingActionApproval;
    if (!approval) return "";

    const toolLabel = this.getToolLabel(approval.toolName);
    const isExternalSend = approval.taskKey === "external.send" || approval.toolName === "gmail";

    return html`
      <div class="action-approval-overlay" @click=${this.handleActionApprovalOverlayClick}>
        <div class="action-approval-modal" @click=${(event: Event) => event.stopPropagation()}>
          <div class="action-approval-icon">${isExternalSend ? "📤" : "⚠️"}</div>
          <div class="action-approval-title">Confirm External Action</div>
          <div class="action-approval-subtitle">
            Your agent wants to perform an external action that may have real-world effects.
            ${isExternalSend ? "This will send a message outside of this session." : ""}
          </div>
          <div class="action-approval-details">
            <strong>Tool:</strong> ${toolLabel}<br>
            <strong>Action Type:</strong> ${approval.actionClass}<br>
            <strong>Reason:</strong> ${approval.reason}
          </div>
          <div class="action-approval-actions">
            <button
              class="action-approval-btn deny"
              @click=${this.handleActionDeny}
            >
              Deny
            </button>
            <button
              class="action-approval-btn approve"
              @click=${this.handleActionApprove}
            >
              Approve
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private getToolLabel(toolName: string): string {
    const labels: Record<string, string> = {
      gmail: "Gmail",
      message: "Messaging",
      exec: "Shell Command",
      write: "File Write",
      edit: "File Edit",
      browser: "Browser Automation",
    };
    return labels[toolName] || toolName;
  }

  private handleActionApprovalOverlayClick = () => {
    // Allow clicking outside to cancel
    this.pendingActionApproval = null;
  };

  private handleActionApprove = async () => {
    const approval = this.pendingActionApproval;
    if (!approval) return;

    this.pendingActionApproval = null;

    // Send an approval confirmation message to continue the action
    const approvalMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: `/approve ${approval.toolName}`,
      timestamp: new Date(),
    };
    this.messages = [...this.messages, approvalMessage];
    this.persistCurrentSession();
    this.scrollToBottom();

    // Send the approval to the agent
    this.inputValue = `/approve ${approval.toolName}`;
    await this.handleSend();
  };

  private handleActionDeny = () => {
    const approval = this.pendingActionApproval;
    if (!approval) return;

    this.pendingActionApproval = null;

    // Add a denial message to the chat
    const denialMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: "assistant",
      content: `Action denied: ${this.getToolLabel(approval.toolName)} was blocked by user.`,
      timestamp: new Date(),
    };
    this.messages = [...this.messages, denialMessage];
    this.persistCurrentSession();
    this.scrollToBottom();
  };

  private handleBack() {
    this.dispatchEvent(new CustomEvent("back", { bubbles: true }));
  }

  private handleNewSession() {
    if (confirm("Start a new conversation? This will clear the current chat.")) {
      this.startNewSession();
    }
  }

  private handleSessionChange(e: Event) {
    const nextSessionId = (e.target as HTMLSelectElement).value;
    this.switchSession(nextSessionId);
  }

  private handleAgentSwitch(e: Event) {
    const nextAgentId = (e.target as HTMLSelectElement).value;
    if (nextAgentId !== this.agentId) {
      this.dispatchEvent(
        new CustomEvent("agent-switch", {
          detail: { agentId: nextAgentId },
          bubbles: true,
        }),
      );
    }
  }

  private looksLikeAutomationIntent(message: string): boolean {
    // Explicit scheduling/notification phrases - always trigger
    if (
      /\b(remind\s*me|alert\s*me|notify\s*me|monitor\s*(for|when)|watch\s*for|check\s*daily|check\s*every)\b/i.test(
        message,
      )
    ) {
      return true;
    }

    // Time-based scheduling: "every morning", "daily at 9am", "weekly on Monday"
    if (
      /\b(daily|weekly|monthly|weekday|weekdays)\s+(at|on|in|check|send|report|summary)\b/i.test(
        message,
      )
    ) {
      return true;
    }

    // "every" + time reference: "every day", "every morning", "every 2 hours"
    if (
      /\bevery\s+(day|morning|evening|night|week|month|hour|minute|\d+\s*(hour|minute|day|week)s?)\b/i.test(
        message,
      )
    ) {
      return true;
    }

    // "if/when" + event triggers: "if I get an email", "when there's a new message"
    // These require automation-specific context, not just the word "if" or "when"
    if (
      /\b(if|when|whenever)\s+(i\s+(get|receive)|there('s| is| are)\s+(a|an|any)?\s*(new\s+)?(email|message|notification|alert|text|update|news)|someone\s+(sends|messages|emails))\b/i.test(
        message,
      )
    ) {
      return true;
    }

    return false;
  }

  private looksLikeIntegrationIntent(message: string): boolean {
    return (
      /\b(set\s*up|enable|configure|connect|use|turn\s*on|activate|add)\s+(imessage|telegram|email|whatsapp|discord|slack|signal)\b/i.test(
        message,
      ) ||
      /\b(imessage|telegram|email|whatsapp|discord|slack|signal)\s+(set\s*up|setup|integration|channel)\b/i.test(
        message,
      ) ||
      /\b(i('d| would)?\s*(like|want)\s*to\s*(use|set\s*up|enable|connect))\s+(imessage|telegram|email)\b/i.test(
        message,
      )
    );
  }

  private detectIntegrationChannel(message: string): string | null {
    const lower = message.toLowerCase();
    if (/imessage|i-?message|sendblue/i.test(lower)) return "imessage";
    if (/telegram/i.test(lower)) return "telegram";
    if (/email|gmail|outlook|imap|smtp/i.test(lower)) return "email";
    return null;
  }

  private showIntegrationSetupPopup(channelId: string) {
    this.integrationChannelId = channelId;
    this.showIntegrationSetup = true;
  }

  private handleIntegrationCancel = () => {
    this.showIntegrationSetup = false;
    this.integrationChannelId = null;
  };

  private handleIntegrationSaved = (detail: { channelId: string; channelName: string }) => {
    this.showIntegrationSetup = false;
    this.integrationChannelId = null;

    // Add a success message to the chat
    const successMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: "assistant",
      content: `${detail.channelName} has been configured successfully! You can now send and receive messages through this channel.`,
      timestamp: new Date(),
    };
    this.messages = [...this.messages, successMsg];
    this.persistCurrentSession();
    this.scrollToBottom();
  };

  private applyAutomationDraft(draft: AutomationRuleDraft) {
    this.automationName = draft.name || "Automation rule";
    this.automationTriggerType = draft.triggerType;
    this.automationSchedule = draft.schedule || "";
    this.automationEventCondition = draft.eventCondition || "";
    this.automationAction = draft.action || "";
    this.automationSourceMessage = draft.sourceMessage || "";
    this.showAutomationConfirm = true;
  }

  private resetAutomationDraftState() {
    this.automationName = "";
    this.automationTriggerType = "schedule";
    this.automationSchedule = "";
    this.automationEventCondition = "";
    this.automationAction = "";
    this.automationSourceMessage = "";
    this.showAutomationConfirm = false;
    this.automationSaving = false;
  }

  private cancelAutomationConfirmation = () => {
    this.resetAutomationDraftState();
  };

  private async confirmAutomationRule() {
    if (this.automationSaving) {
      return;
    }
    const name = this.automationName.trim();
    const action = this.automationAction.trim();
    const schedule = this.automationSchedule.trim();
    const eventCondition = this.automationEventCondition.trim();
    if (!name || !action) {
      return;
    }
    if (this.automationTriggerType === "schedule" && !schedule) {
      return;
    }
    if (this.automationTriggerType === "event" && !eventCondition) {
      return;
    }

    this.automationSaving = true;
    try {
      const result = await raxApi.createAutomationRule({
        name,
        triggerType: this.automationTriggerType,
        schedule: this.automationTriggerType === "schedule" ? schedule : undefined,
        eventCondition: this.automationTriggerType === "event" ? eventCondition : undefined,
        action,
        sourceMessage: this.automationSourceMessage || undefined,
        agentId: this.agentId || undefined,
      });

      if (!result.success || !result.data) {
        const failureMessage: ChatMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: `⚠️ Could not save automation rule: ${result.error || "Unknown error"}`,
          timestamp: new Date(),
        };
        this.messages = [...this.messages, failureMessage];
        this.persistCurrentSession();
        this.scrollToBottom();
        return;
      }

      const createdRule = result.data;
      this.automationRules = [
        createdRule,
        ...this.automationRules.filter((rule) => rule.id !== createdRule.id),
      ];

      const triggerSummary =
        this.automationTriggerType === "schedule"
          ? `Schedule: ${schedule}`
          : `Event: ${eventCondition}`;
      const successMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: [`✅ Automation saved: **${name}**`, triggerSummary, `Action: ${action}`].join(
          "\n",
        ),
        timestamp: new Date(),
      };
      this.messages = [...this.messages, successMessage];
      this.persistCurrentSession();
      this.scrollToBottom();
    } finally {
      this.resetAutomationDraftState();
    }
  }

  private closeAutomationRules = () => {
    this.showAutomationRules = false;
  };

  private toggleAutomationRules = async () => {
    this.showAutomationRules = !this.showAutomationRules;
    if (this.showAutomationRules) {
      this.showSharePanel = false;
      await this.loadAutomationRules();
    }
  };

  private closeSharePanel = () => {
    this.showSharePanel = false;
  };

  private toggleSharePanel = async () => {
    this.showSharePanel = !this.showSharePanel;
    if (this.showSharePanel) {
      this.showAutomationRules = false;
      await this.loadShareData();
    }
  };

  private formatShareRole(role: AgentShareRole): string {
    switch (role) {
      case "viewer":
        return "Viewer";
      case "member":
      case "user":
        return "Member";
      case "collaborator":
      case "editor":
        return "Collaborator";
      case "owner":
      case "admin":
        return "Owner";
      default:
        return role;
    }
  }

  private describeShareScope(share: AgentShare): string {
    const permissions = share.skillPermissions;
    if (!permissions || typeof permissions !== "object") {
      return "Scope: all enabled skills";
    }

    const parts = Object.entries(permissions).map(([skillId, rawPermission]) => {
      let read = false;
      let write = false;
      if (Array.isArray(rawPermission)) {
        const normalized = rawPermission.map((entry) => String(entry).trim().toLowerCase());
        write = normalized.includes("write");
        read = write || normalized.includes("read");
      } else if (rawPermission && typeof rawPermission === "object") {
        const candidate = rawPermission as { read?: unknown; write?: unknown };
        write = Boolean(candidate.write);
        read = write || Boolean(candidate.read);
      }

      const mode = write ? "read/write" : read ? "read-only" : "blocked";
      return `${skillId} (${mode})`;
    });

    if (parts.length === 0) {
      return "Scope: all enabled skills";
    }
    return `Scope: ${parts.join(", ")}`;
  }

  private toggleScopedSkill(skillId: string, checked: boolean) {
    if (checked) {
      if (!this.selectedScopeSkills.includes(skillId)) {
        this.selectedScopeSkills = [...this.selectedScopeSkills, skillId];
      }
      return;
    }
    this.selectedScopeSkills = this.selectedScopeSkills.filter((value) => value !== skillId);
  }

  private async loadShareData() {
    if (!this.agentId) {
      return;
    }

    this.shareLoading = true;
    this.shareError = null;
    try {
      const [sharesResult, agentResult] = await Promise.all([
        raxApi.listAgentShares(this.agentId),
        raxApi.getAgent(this.agentId),
      ]);

      if (!sharesResult.success || !Array.isArray(sharesResult.data)) {
        this.shareError = sharesResult.error || "Failed to load shares.";
        this.shares = [];
      } else {
        this.shares = sharesResult.data;
      }

      if (agentResult.success && agentResult.data) {
        const enabled = Array.isArray(agentResult.data.enabledSkills)
          ? agentResult.data.enabledSkills.filter(
              (value): value is string => typeof value === "string",
            )
          : [];
        this.availableSkills = [...new Set(enabled)];
        this.selectedScopeSkills = this.selectedScopeSkills.filter((skillId) =>
          this.availableSkills.includes(skillId),
        );
      } else {
        this.availableSkills = [];
        this.selectedScopeSkills = [];
      }
    } catch (error) {
      this.shareError = String(error);
      this.shares = [];
      this.availableSkills = [];
      this.selectedScopeSkills = [];
    } finally {
      this.shareLoading = false;
    }
  }

  private submitShareInvite = async () => {
    if (!this.agentId || this.shareSubmitting) {
      return;
    }
    const email = this.shareInviteEmail.trim();
    if (!email) {
      this.shareError = "Email is required.";
      return;
    }

    this.shareSubmitting = true;
    this.shareError = null;
    try {
      let skillPermissions: Record<string, { read: boolean; write: boolean }> | undefined;
      if (this.shareScopeMode === "scoped") {
        if (this.selectedScopeSkills.length === 0) {
          this.shareError = "Select at least one skill for scoped sharing.";
          return;
        }
        skillPermissions = {};
        for (const skillId of this.selectedScopeSkills) {
          skillPermissions[skillId] = { read: true, write: this.shareWriteAccess };
        }
      }

      const result = await raxApi.shareAgent(this.agentId, {
        email,
        role: this.shareInviteRole,
        skillPermissions,
      });

      if (!result.success || !result.data) {
        this.shareError = result.error || "Failed to share agent.";
        return;
      }

      this.shares = [
        result.data,
        ...this.shares.filter((entry) => entry.userId !== result.data!.userId),
      ];
      this.shareInviteEmail = "";
      this.shareScopeMode = "all";
      this.selectedScopeSkills = [];
      this.shareWriteAccess = true;
    } catch (error) {
      this.shareError = String(error);
    } finally {
      this.shareSubmitting = false;
    }
  };

  private async removeShareAccess(shareUserId: string) {
    if (!this.agentId || this.shareSubmitting) {
      return;
    }
    this.shareSubmitting = true;
    this.shareError = null;
    try {
      const result = await raxApi.removeAgentShare(this.agentId, shareUserId);
      if (!result.success) {
        this.shareError = result.error || "Failed to remove share.";
        return;
      }
      this.shares = this.shares.filter((entry) => entry.userId !== shareUserId);
    } catch (error) {
      this.shareError = String(error);
    } finally {
      this.shareSubmitting = false;
    }
  }

  private async loadAutomationRules() {
    this.automationRulesLoading = true;
    this.automationRulesError = null;
    try {
      const result = await raxApi.listAutomationRules();
      if (!result.success || !Array.isArray(result.data)) {
        this.automationRulesError = result.error || "Failed to load rules.";
        this.automationRules = [];
        return;
      }
      const forAgent = result.data.filter((rule) => !rule.agentId || rule.agentId === this.agentId);
      this.automationRules = forAgent;
    } catch (error) {
      this.automationRulesError = String(error);
      this.automationRules = [];
    } finally {
      this.automationRulesLoading = false;
    }
  }

  private async updateAutomationRuleStatus(ruleId: string, status: AutomationRuleStatus) {
    try {
      const result = await raxApi.updateAutomationRuleStatus(ruleId, status);
      if (!result.success || !result.data) {
        this.automationRulesError = result.error || "Failed to update status.";
        return;
      }
      this.automationRules = this.automationRules.map((rule) =>
        rule.id === ruleId ? result.data! : rule,
      );
    } catch (error) {
      this.automationRulesError = String(error);
    }
  }

  private async deleteAutomationRule(ruleId: string) {
    try {
      const result = await raxApi.deleteAutomationRule(ruleId);
      if (!result.success) {
        this.automationRulesError = result.error || "Failed to delete rule.";
        return;
      }
      this.automationRules = this.automationRules.filter((rule) => rule.id !== ruleId);
    } catch (error) {
      this.automationRulesError = String(error);
    }
  }

  private handleInput(e: InputEvent) {
    this.inputValue = (e.target as HTMLTextAreaElement).value;
    this.autoResizeTextarea(e.target as HTMLTextAreaElement);
  }

  private removeImageAttachment(attachmentId: string) {
    this.imageAttachments = this.imageAttachments.filter((item) => item.id !== attachmentId);
    if (this.imageAttachments.length === 0 && this.documentAttachments.length === 0) {
      this.attachmentWarning = null;
    }
  }

  private removeDocumentAttachment(attachmentId: string) {
    this.documentAttachments = this.documentAttachments.filter((item) => item.id !== attachmentId);
    if (this.imageAttachments.length === 0 && this.documentAttachments.length === 0) {
      this.attachmentWarning = null;
    }
  }

  private async readFileAsDataUrl(file: File): Promise<string> {
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = typeof reader.result === "string" ? reader.result : "";
        if (!result) {
          reject(new Error("Could not read image attachment."));
          return;
        }
        resolve(result);
      };
      reader.onerror = () => reject(new Error("Could not read image attachment."));
      reader.readAsDataURL(file);
    });
  }

  private handleUploadClick(): void {
    const fileInput = this.shadowRoot?.querySelector<HTMLInputElement>('.hidden-file-input');
    if (fileInput) {
      fileInput.click();
    }
  }

  private async handleFileInputChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    if (files.length === 0) return;

    // Clear the input so the same file can be selected again
    input.value = '';

    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    const otherFiles = files.filter(f => !f.type.startsWith('image/'));

    // Handle image files
    const nextAttachments: ChatImageAttachment[] = [];
    for (const file of imageFiles) {
      if (file.size > 650_000) {
        this.attachmentWarning = `Image "${file.name}" is too large. Max ~650KB.`;
        continue;
      }
      try {
        const dataUrl = await this.readFileAsDataUrl(file);
        nextAttachments.push({
          id: crypto.randomUUID(),
          dataUrl,
          mimeType: file.type || "image/*",
          name: file.name,
          sizeBytes: file.size,
        });
      } catch {
        this.attachmentWarning = `Could not read image "${file.name}".`;
      }
    }

    if (nextAttachments.length > 0) {
      this.imageAttachments = [...this.imageAttachments, ...nextAttachments];
      this.attachmentWarning = null;
    }

    // Handle non-image files
    const nextDocAttachments: ChatDocumentAttachment[] = [];

    for (const file of otherFiles) {
      const ext = file.name.toLowerCase().match(/\.[^.]+$/)?.[0] || '';
      const documentMime = DOCUMENT_MIME_TYPES[ext];

      // Check if this is a supported document type (xlsx, docx, pptx, pdf, csv)
      if (documentMime) {
        // Document files - upload as base64 for Claude to process
        if (file.size > 10_000_000) {
          this.attachmentWarning = `Document "${file.name}" is too large. Max 10MB.`;
          continue;
        }
        try {
          const dataUrl = await this.readFileAsDataUrl(file);
          nextDocAttachments.push({
            id: crypto.randomUUID(),
            dataUrl,
            mimeType: documentMime,
            name: file.name,
            sizeBytes: file.size,
          });
        } catch {
          this.attachmentWarning = `Could not read document "${file.name}".`;
        }
        continue;
      }

      // Unsupported binary files
      const unsupportedBinary = new Set(['.zip', '.rar', '.7z', '.tar', '.gz', '.exe', '.dll', '.so', '.dylib']);
      if (unsupportedBinary.has(ext)) {
        this.attachmentWarning = `Binary file "${file.name}" cannot be attached. Only documents (Excel, Word, PowerPoint, PDF, CSV) and images are supported.`;
        continue;
      }

      // Plain text files - add inline
      if (file.size > 1_000_000) {
        this.attachmentWarning = `File "${file.name}" is too large. Max 1MB for text files.`;
        continue;
      }
      try {
        const text = await file.text();
        // Verify it's actually valid text (not binary garbage)
        const invalidChars = text.match(/[\x00-\x08\x0E-\x1F]/g);
        if (invalidChars && invalidChars.length > text.length * 0.01) {
          this.attachmentWarning = `File "${file.name}" appears to be binary. Only documents (Excel, Word, PowerPoint, PDF, CSV) and images are supported.`;
          continue;
        }
        // Add file contents as an attachment marker in the input
        const marker = `\n📎 File: ${file.name}\n\`\`\`\n${text.slice(0, 5000)}${text.length > 5000 ? '\n...(truncated)' : ''}\n\`\`\`\n`;
        this.inputValue += marker;
        this.attachmentWarning = null;
      } catch {
        this.attachmentWarning = `Could not read file "${file.name}".`;
      }
    }

    if (nextDocAttachments.length > 0) {
      this.documentAttachments = [...this.documentAttachments, ...nextDocAttachments];
      this.attachmentWarning = null;
    }
  }

  private async handleInputPaste(event: ClipboardEvent) {
    if (this.sending) {
      return;
    }
    const clipboardItems = Array.from(event.clipboardData?.items || []);
    const imageItems = clipboardItems.filter((item) => item.type.startsWith("image/"));
    if (imageItems.length === 0) {
      return;
    }

    event.preventDefault();
    const nextAttachments: ChatImageAttachment[] = [];
    const remainingSlots = Math.max(0, 3 - this.imageAttachments.length);
    if (remainingSlots === 0) {
      this.attachmentWarning = "Maximum 3 image attachments per message.";
      return;
    }

    for (const item of imageItems.slice(0, remainingSlots)) {
      const file = item.getAsFile();
      if (!file) {
        continue;
      }
      if (file.size > 650_000) {
        this.attachmentWarning = `Image "${file.name || "attachment"}" is too large. Max ~650KB.`;
        continue;
      }
      try {
        const dataUrl = await this.readFileAsDataUrl(file);
        nextAttachments.push({
          id: crypto.randomUUID(),
          dataUrl,
          mimeType: file.type || "image/*",
          name: file.name || "pasted-image",
          sizeBytes: file.size,
        });
      } catch {
        this.attachmentWarning = "Could not read one of the pasted images.";
      }
    }

    if (nextAttachments.length > 0) {
      this.imageAttachments = [...this.imageAttachments, ...nextAttachments];
      this.attachmentWarning = null;
    }
  }

  private handleKeydown(e: KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      this.handleSend();
    }
  }

  private autoResizeTextarea(textarea: HTMLTextAreaElement) {
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
  }

  private buildHistoryPayload(): ChatHistoryPayloadMessage[] {
    const HISTORY_MAX_MESSAGES = 16;
    const HISTORY_MAX_CONTENT_CHARS = 6000;
    const eligible = this.messages.filter(
      (message) => message.role === "user" || message.role === "assistant",
    );
    const priorMessages = eligible.slice(0, -1).slice(-HISTORY_MAX_MESSAGES);
    return priorMessages.map((message) => {
      const trimmedContent = message.content.trim();
      return {
        role: message.role as "user" | "assistant",
        content:
          trimmedContent.length > HISTORY_MAX_CONTENT_CHARS
            ? `${trimmedContent.slice(0, HISTORY_MAX_CONTENT_CHARS)}…`
            : trimmedContent,
      };
    });
  }

  private async handleSend() {
    const content = this.inputValue.trim();
    const imageAttachments = [...this.imageAttachments];
    const documentAttachments = [...this.documentAttachments];
    if (!content && imageAttachments.length === 0 && documentAttachments.length === 0) return;

    // If already sending, queue the message for later
    if (this.sending) {
      this.messageQueue = [...this.messageQueue, { content, images: imageAttachments }];
      this.inputValue = "";
      this.imageAttachments = [];
      this.documentAttachments = [];
      return;
    }
    let assistantMsg: ChatMessage | null = null;

    // Build display content with attachment counts
    const attachmentParts: string[] = [];
    if (imageAttachments.length > 0) {
      attachmentParts.push(`${imageAttachments.length} image${imageAttachments.length === 1 ? "" : "s"}`);
    }
    if (documentAttachments.length > 0) {
      const docNames = documentAttachments.map(d => d.name).join(", ");
      attachmentParts.push(`${documentAttachments.length} document${documentAttachments.length === 1 ? "" : "s"} (${docNames})`);
    }
    const userDisplayContent = attachmentParts.length > 0
      ? `${content || "[Attachment]"}\n\n📎 ${attachmentParts.join(", ")} attached`
      : content;

    // Add user message
    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: userDisplayContent,
      timestamp: new Date(),
    };
    this.messages = [...this.messages, userMsg];
    this.persistCurrentSession();
    this.inputValue = "";
    this.imageAttachments = [];
    this.documentAttachments = [];
    this.attachmentWarning = null;

    // Dispatch user-message-sent event for task detection
    this.dispatchEvent(
      new CustomEvent("user-message-sent", {
        detail: { agentId: this.agentId, message: content },
        bubbles: true,
        composed: true,
      }),
    );

    if (content && this.looksLikeAutomationIntent(content)) {
      try {
        const draftResult = await raxApi.draftAutomationRule(content, this.agentId || undefined);
        if (draftResult.success && draftResult.data?.detected && draftResult.data.draft) {
          this.applyAutomationDraft(draftResult.data.draft);
          const promptMessage: ChatMessage = {
            id: crypto.randomUUID(),
            role: "assistant",
            content:
              "We can definitely set that up. Please confirm or edit the pre-filled details in the popup.",
            timestamp: new Date(),
          };
          this.messages = [...this.messages, promptMessage];
          this.persistCurrentSession();
          this.scrollToBottom();
          return;
        }
      } catch {
        // Non-blocking: continue with normal chat path if draft detection fails.
      }
    }

    // Check for integration setup intent
    if (content && this.looksLikeIntegrationIntent(content)) {
      const channelId = this.detectIntegrationChannel(content);
      if (channelId) {
        // Ask user how they want to proceed
        const promptMessage: ChatMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content:
            `Great! I can help you set up ${channelId === "imessage" ? "iMessage" : channelId}. Would you like:\n\n` +
            `**Guided setup** - A form with instructions and fields to fill in\n\n` +
            `**Chat configuration** - I'll walk you through it step by step\n\n` +
            `Just say "guided" or "chat" to continue.`,
          timestamp: new Date(),
        };
        this.messages = [...this.messages, promptMessage];
        this.persistCurrentSession();
        this.scrollToBottom();

        // Store the pending channel for follow-up
        (this as any)._pendingIntegrationChannel = channelId;
        return;
      }
    }

    // Check for guided setup response
    if (content && (this as any)._pendingIntegrationChannel) {
      const lower = content.toLowerCase();
      if (lower.includes("guided") || lower.includes("form") || lower.includes("popup")) {
        const channelId = (this as any)._pendingIntegrationChannel;
        delete (this as any)._pendingIntegrationChannel;
        this.showIntegrationSetupPopup(channelId);

        const confirmMsg: ChatMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: "Opening the setup form for you now.",
          timestamp: new Date(),
        };
        this.messages = [...this.messages, confirmMsg];
        this.persistCurrentSession();
        this.scrollToBottom();
        return;
      } else if (lower.includes("chat") || lower.includes("walk") || lower.includes("step")) {
        const channelId = (this as any)._pendingIntegrationChannel;
        delete (this as any)._pendingIntegrationChannel;

        // Continue to normal chat - the AI will help them through chat
        const continueMsg: ChatMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content:
            `Okay, let's set up ${channelId === "imessage" ? "iMessage via Sendblue" : channelId} together. ` +
            `I'll need a few things from you. First, do you have your API credentials ready?`,
          timestamp: new Date(),
        };
        this.messages = [...this.messages, continueMsg];
        this.persistCurrentSession();
        this.scrollToBottom();
        return;
      }
    }

    this.sending = true;
    this.typing = true;
    this.thinkingStatus = "Thinking"; // Reset to default
    // Notify parent that we're actively processing
    this.dispatchEvent(
      new CustomEvent("session-status-change", {
        bubbles: true,
        composed: true,
        detail: { status: "thinking" },
      }),
    );

    // Scroll to show typing indicator
    this.scrollToBottom();

    try {
      const history = this.buildHistoryPayload();
      // Get auth token
      const clerk = (window as any).clerkClient || (window as any).Clerk;
      const token = clerk?.session ? await clerk.session.getToken() : null;

      const res = await fetch("/api/rax/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message: content || (documentAttachments.length > 0 ? "Please analyze the attached document(s)." : "Analyze the attached image(s)."),
          sessionId: this.sessionId,
          agentId: this.agentId || undefined,
          history,
          images: imageAttachments.map((image) => ({
            dataUrl: image.dataUrl,
            mimeType: image.mimeType,
            name: image.name,
            sizeBytes: image.sizeBytes,
          })),
          documents: documentAttachments.map((doc) => ({
            dataUrl: doc.dataUrl,
            mimeType: doc.mimeType,
            name: doc.name,
          })),
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Unknown error" }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }

      // Read SSE stream
      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let streamErrorText: string | null = null;
      let streamErrorShown = false;
      const seenToolEventKeys = new Set<string>();

      const extractStreamErrorText = (payload: unknown): string | null => {
        if (!payload || typeof payload !== "object") {
          return null;
        }
        const root = payload as Record<string, unknown>;
        const rootError = root.error;
        if (typeof rootError === "string" && rootError.trim()) {
          return rootError.trim();
        }
        if (rootError && typeof rootError === "object") {
          const errorObj = rootError as Record<string, unknown>;
          const message = errorObj.message;
          if (typeof message === "string" && message.trim()) {
            return message.trim();
          }
          const detail = errorObj.detail;
          if (typeof detail === "string" && detail.trim()) {
            return detail.trim();
          }
        }
        return null;
      };

      const processSseEventBlock = (eventBlock: string) => {
        const lines = eventBlock.split(/\r?\n/);
        const dataLines: string[] = [];
        for (const line of lines) {
          if (!line.startsWith("data:")) {
            continue;
          }
          dataLines.push(line.slice(5).trimStart());
        }
        if (dataLines.length === 0) {
          return;
        }
        const data = dataLines.join("\n").trim();
        if (!data || data === "[DONE]") {
          return;
        }

        try {
          const parsed = JSON.parse(data);
          const streamError = extractStreamErrorText(parsed);
          if (streamError) {
            const normalizedError = this.normalizeStreamError(streamError);
            streamErrorText = normalizedError;
            if (!assistantMsg) {
              this.typing = false;
              assistantMsg = {
                id: crypto.randomUUID(),
                role: "assistant",
                content: `⚠️ ${normalizedError}`,
                timestamp: new Date(),
              };
              this.messages = [...this.messages, assistantMsg];
              this.persistCurrentSession();
              streamErrorShown = true;
              this.scrollToBottom();
            } else if (!streamErrorShown) {
              assistantMsg.content += `\n\n⚠️ ${normalizedError}`;
              this.messages = [...this.messages];
              this.persistCurrentSession();
              streamErrorShown = true;
              this.scrollToBottom();
            }
            return;
          }

          const extractToolEventFromPayload = (
            payload: Record<string, unknown>,
          ): StreamToolEvent | null => {
            const wrapped = payload.tool_event as Record<string, unknown> | undefined;
            const candidate = wrapped && typeof wrapped === "object" ? wrapped : payload;
            const phase = typeof candidate.phase === "string" ? candidate.phase : "";
            const name = typeof candidate.name === "string" ? candidate.name : undefined;
            if (!phase || !name) {
              return null;
            }

            let meta = typeof candidate.meta === "string" ? candidate.meta.trim() : "";
            if (!meta) {
              const result = candidate.result as Record<string, unknown> | undefined;
              const details = result?.details as Record<string, unknown> | undefined;
              const statusText =
                typeof details?.statusText === "string" ? details.statusText.trim() : "";
              if (statusText) {
                meta = statusText;
              } else {
                const content = Array.isArray(result?.content) ? result.content : [];
                const textParts = content
                  .map((part) => {
                    if (!part || typeof part !== "object") return "";
                    const item = part as Record<string, unknown>;
                    if (item.type !== "text" || typeof item.text !== "string") return "";
                    return item.text.trim();
                  })
                  .filter((value) => value.length > 0);
                if (textParts.length > 0) {
                  meta = textParts.join("\n");
                }
              }
            }

            // Extract tool args for enhanced rendering (especially for sessions_spawn)
            const args =
              candidate.args && typeof candidate.args === "object"
                ? (candidate.args as Record<string, unknown>)
                : undefined;

            const isError =
              candidate.isError === true ||
              (
                (candidate.result as Record<string, unknown> | undefined)?.details as
                  | Record<string, unknown>
                  | undefined
              )?.status === "error";
            return {
              phase,
              name,
              meta: meta || undefined,
              isError,
              args,
            };
          };

          // Check for approval required in tool result
          const checkApprovalRequired = (
            payload: Record<string, unknown>,
          ): {
            toolName: string;
            taskKey: string;
            reason: string;
            actionClass: string;
          } | null => {
            const wrapped = payload.tool_event as Record<string, unknown> | undefined;
            const candidate = wrapped && typeof wrapped === "object" ? wrapped : payload;
            const result = candidate.result as Record<string, unknown> | undefined;

            // Look for approvalRequired in the result content
            if (result?.content && Array.isArray(result.content)) {
              for (const part of result.content) {
                if (!part || typeof part !== "object") continue;
                const item = part as Record<string, unknown>;
                if (item.type !== "text" || typeof item.text !== "string") continue;
                try {
                  const parsed = JSON.parse(item.text);
                  if (parsed.approvalRequired === true && parsed.blocked === true) {
                    return {
                      toolName: String(parsed.tool || candidate.name || "tool"),
                      taskKey: String(parsed.policy?.taskKey || "external.send"),
                      reason: String(
                        parsed.policy?.reason || parsed.error || "Action requires approval",
                      ),
                      actionClass: String(parsed.policy?.actionClass || "do"),
                    };
                  }
                } catch {
                  // Not JSON, continue
                }
              }
            }
            return null;
          };

          const approvalRequired = checkApprovalRequired(parsed);
          if (approvalRequired && !this.pendingActionApproval) {
            this.pendingActionApproval = approvalRequired;
            this.scrollToBottom();
            return; // Don't process this as a normal event
          }

          // Check for turn_cost event (emitted before [DONE])
          if (parsed.turn_cost && assistantMsg) {
            const turnCostData = parsed.turn_cost as {
              costCents?: number;
              tokensIn?: number;
              tokensOut?: number;
              model?: string;
            };
            if (typeof turnCostData.costCents === "number" && turnCostData.costCents > 0) {
              // Convert cents to dollars for display
              assistantMsg.cost = turnCostData.costCents / 100;
              this.messages = [...this.messages];
            }
            return;
          }

          // Handle persona events from harness (for pixel office animations AND step transparency)
          if (parsed.stream === "persona" && parsed.data) {
            const personaData = parsed.data as {
              type?: string;
              personaId?: string;
              task?: string;
              success?: boolean;
              message?: string;
              tool?: string;
              phase?: string;
            };
            const eventType = personaData.type;

            // Handle pixel office persona animations
            const personaId = personaData.personaId;
            if (eventType && personaId) {
              if (eventType === "persona_spawned") {
                window.dispatchEvent(
                  new CustomEvent("persona-spawned", {
                    detail: {
                      personaId,
                      runId: this.sessionId,
                      task: personaData.task,
                    },
                    bubbles: true,
                  }),
                );
              } else if (eventType === "persona_completed" || eventType === "persona_error") {
                window.dispatchEvent(
                  new CustomEvent("persona-completed", {
                    detail: {
                      personaId,
                      runId: this.sessionId,
                      success: eventType === "persona_completed",
                      task: personaData.task,
                    },
                    bubbles: true,
                  }),
                );
              }
            }

            // Handle progress/thinking events (step transparency)
            if (eventType === "thinking" || eventType === "tool_progress" || eventType === "tool_start") {
              const toolLabel = personaData.tool ? ` (${personaData.tool})` : "";
              const progressMsg = personaData.message || `Working${toolLabel}...`;
              this.thinkingStatus = progressMsg;
              this.requestUpdate();
            }

            return;
          }

          // Handle file events from harness (generated documents)
          if (parsed.stream === "file" && parsed.data) {
            const fileData = parsed.data as {
              name?: string;
              type?: string;
              size?: number;
              mimeType?: string;
              downloadUrl?: string;
            };
            if (fileData.downloadUrl && fileData.name) {
              // Ensure we have an assistant message to append to
              if (!assistantMsg) {
                assistantMsg = {
                  id: crypto.randomUUID(),
                  role: "assistant",
                  content: "",
                  timestamp: new Date(),
                };
                this.messages = [...this.messages, assistantMsg];
              }
              // Render download link as styled HTML
              const fileIcon = {
                xlsx: "📊",
                pptx: "📽️",
                docx: "📄",
                pdf: "📑",
              }[fileData.type || ""] || "📁";
              const sizeKb = fileData.size ? Math.round(fileData.size / 1024) : 0;
              const sizeLabel = sizeKb > 0 ? ` (${sizeKb} KB)` : "";
              // Transform harness URL (/api/files/...) to control plane proxy URL (/api/rax/files/...)
              const harnessPath = fileData.downloadUrl.replace(/^\/api\/files\//, "");
              const fullUrl = `/api/rax/files/${harnessPath}`;
              assistantMsg.content += `\n\n<div class="generated-file-download">
<span class="file-icon">${fileIcon}</span>
<a href="${fullUrl}" download="${fileData.name}" class="file-download-link" target="_blank">
<strong>${fileData.name}</strong>${sizeLabel}
</a>
<span class="download-hint">Click to download</span>
</div>\n`;
              this.messages = [...this.messages];
              this.scrollToBottom();
            }
            return;
          }

          const delta = parsed.choices?.[0]?.delta?.content;
          const toolEvent = extractToolEventFromPayload(parsed);
          if (delta || toolEvent) {
            if (!assistantMsg) {
              this.typing = false;
              assistantMsg = {
                id: crypto.randomUUID(),
                role: "assistant",
                content: "",
                timestamp: new Date(),
              };
              this.messages = [...this.messages, assistantMsg];
            }

            if (delta) {
              assistantMsg.content += delta;
              // Emit typing status for pixel-office character animation
              this.dispatchEvent(
                new CustomEvent("session-status-change", {
                  bubbles: true,
                  composed: true,
                  detail: { status: "running", tool: "typing" },
                }),
              );
            }
            if (toolEvent?.name) {
              const toolEventKey = [
                toolEvent.phase,
                toolEvent.name,
                toolEvent.meta || "",
                toolEvent.isError ? "1" : "0",
              ].join("|");
              if (!seenToolEventKeys.has(toolEventKey)) {
                seenToolEventKeys.add(toolEventKey);
                const renderedToolEvent = renderToolEventTranscriptLine(toolEvent);
                if (renderedToolEvent) {
                  assistantMsg.content += renderedToolEvent;
                }
                // Emit real-time activity event for pixel-office character animation
                const isToolActive = toolEvent.phase === "start" || toolEvent.phase === "running";
                this.dispatchEvent(
                  new CustomEvent("session-status-change", {
                    bubbles: true,
                    composed: true,
                    detail: {
                      status: isToolActive ? "running" : "thinking",
                      tool: isToolActive ? toolEvent.name : undefined,
                    },
                  }),
                );
              }
            }
            this.messages = [...this.messages];
            this.scrollToBottom();
          }
        } catch {
          // Ignore malformed chunks and continue stream consumption.
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        while (true) {
          const lfBoundary = buffer.indexOf("\n\n");
          const crlfBoundary = buffer.indexOf("\r\n\r\n");
          let boundaryIndex = -1;
          let boundarySize = 0;
          if (lfBoundary !== -1 && (crlfBoundary === -1 || lfBoundary < crlfBoundary)) {
            boundaryIndex = lfBoundary;
            boundarySize = 2;
          } else if (crlfBoundary !== -1) {
            boundaryIndex = crlfBoundary;
            boundarySize = 4;
          }
          if (boundaryIndex === -1) {
            break;
          }
          const eventBlock = buffer.slice(0, boundaryIndex);
          buffer = buffer.slice(boundaryIndex + boundarySize);
          if (!eventBlock.trim()) {
            continue;
          }
          processSseEventBlock(eventBlock);
        }
      }

      if (buffer.trim().length > 0) {
        processSseEventBlock(buffer);
      }

      if (!assistantMsg) {
        const fallbackMessage =
          streamErrorText ||
          "Your agent returned no message. This is usually an upstream provider failure (quota/auth/rate-limit). Check Models and retry.";
        assistantMsg = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: `⚠️ ${fallbackMessage}`,
          timestamp: new Date(),
        };
        this.messages = [...this.messages, assistantMsg];
        this.persistCurrentSession();
      }
    } catch (err) {
      this.typing = false;
      if (!assistantMsg) {
        // No content streamed yet, show error as assistant message
        const errorMsg: ChatMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: `⚠️ ${this.normalizeStreamError(
            err instanceof Error ? err.message : "Failed to reach your agent",
          )}`,
          timestamp: new Date(),
        };
        this.messages = [...this.messages, errorMsg];
        this.persistCurrentSession();
      }
    } finally {
      this.sending = false;
      this.typing = false;
      this.persistCurrentSession();
      this.scrollToBottom(true); // Force scroll to show final result
      void this.emitSessionCostUpdate();
      // Emit idle status when stream completes for pixel-office animation
      this.dispatchEvent(
        new CustomEvent("session-status-change", {
          bubbles: true,
          composed: true,
          detail: { status: "idle" },
        }),
      );
      // Process any queued messages
      this.processQueue();
    }
  }

  private async emitSessionCostUpdate() {
    try {
      const result = await raxApi.getSessionCost(this.sessionId);
      if (!result.success || !result.data) {
        return;
      }

      this.dispatchEvent(
        new CustomEvent("session-cost-update", {
          bubbles: true,
          composed: true,
          detail: {
            sessionKey: this.sessionId,
            totalCostUsd: result.data.totalCostUsd,
            llmCalls: result.data.llmCalls,
            toolCalls: result.data.toolCalls,
            completed: true, // Signal that the turn is complete
          },
        }),
      );
    } catch {
      // Ignore non-critical UI cost update errors.
    }
  }

  private processQueue() {
    if (this.messageQueue.length === 0) return;

    // Take the first message from the queue
    const [nextMessage, ...remaining] = this.messageQueue;
    this.messageQueue = remaining;

    // Restore the message to the input and trigger send
    this.inputValue = nextMessage.content;
    this.imageAttachments = nextMessage.images;

    // Use setTimeout to allow state to settle before sending
    setTimeout(() => {
      void this.handleSend();
    }, 100);
  }

  private scrollToBottom(force = false) {
    // Use multiple frames to ensure DOM has been fully updated
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const messagesEl = this.shadowRoot?.querySelector(".messages");
        if (messagesEl) {
          const isNearBottom =
            messagesEl.scrollTop + messagesEl.clientHeight >= messagesEl.scrollHeight - 100;
          // Only auto-scroll if user hasn't scrolled up (unless forced)
          if (force || isNearBottom) {
            messagesEl.scrollTo({
              top: messagesEl.scrollHeight,
              behavior: force ? "auto" : "smooth",
            });
          }
        }
      });
    });
  }

  public addMessage(msg: ChatMessage) {
    this.messages = [...this.messages, msg];
    this.typing = false;
    this.sending = false;
  }

  private normalizeStreamError(rawMessage: string): string {
    const message = rawMessage.trim();
    const normalized = message.toLowerCase();
    if (
      /(insufficient_quota|exceeded your current quota|billing details|check your plan|quota)/i.test(
        normalized,
      )
    ) {
      return "Provider quota exceeded. Add credits in your provider billing settings or switch models in Models, then retry.";
    }
    if (/(rate limit|too many requests|throttle)/i.test(normalized)) {
      return "Provider rate limit hit. Retry in a few seconds or switch to another model.";
    }
    if (/(unauthorized|invalid api key|authentication|forbidden)/i.test(normalized)) {
      return "Provider authentication failed. Update your model/API key in Connections or Models, then retry.";
    }
    return message || "Failed to reach your agent";
  }

  private formatTime(date: Date): string {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const isToday = messageDate.getTime() === today.getTime();
    const timeString = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    if (isToday) {
      return timeString;
    } else {
      const dateString = date.toLocaleDateString([], { month: "short", day: "numeric" });
      return `${dateString} ${timeString}`;
    }
  }

  private formatCost(usd: number): string {
    if (usd === 0) return "$0.00";
    if (usd < 0.0001) return `$${usd.toFixed(5)}`;
    if (usd < 0.01) return `$${usd.toFixed(4)}`;
    if (usd < 0.1) return `$${usd.toFixed(3)}`;
    return `$${usd.toFixed(2)}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-agent-chat": RaxAgentChat;
  }
}
